import {
  AdditiveBlending,
  Box3,
  BufferGeometry,
  Color,
  Euler,
  FrontSide,
  Frustum,
  Group,
  Line,
  LineBasicMaterial,
  type Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  NoColorSpace,
  SRGBColorSpace,
  Object3D,
  PerspectiveCamera,
  Quaternion,
  RepeatWrapping,
  Ray,
  Raycaster,
  Sprite,
  Texture,
  TextureLoader,
  Vector3,
  type Intersection,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { Ioc } from "@core/ioc/ioc";
import { FRAME_MODEL, type FrameModel } from "@core/frame/frame";
import { PICKING_SERVICE, type PickingService } from "@runtime/picking/pickingService";
import type { ThreeEngine } from "@runtime/engine/engineService";
import { createAvatarGlowMaterial, configureAvatarHeadShadowCast } from "./avatarGlowShader";
import { createNameTag, disposeNameTag } from "./networkUsersRenderer";
import {
  NaturalHeadIntrusionReaction,
  NaturalIdleHeadMotion,
  NaturalUserCheckIn,
  type IdleVisualCue,
} from "./sparkIdleMotion";

import { publicUrl } from "@core/publicUrl";
/**
 * `SparkBehavior` — port of Unity's `AI/Spark/SparkBehavior.cs` +
 * `SparkBehavior.prefab`.
 *
 * Spark is a virtual participant standing near the user: a mannequin head
 * (same `head13` mesh + fresnel glow the multiuser presence layer uses)
 * that occupies a socially appropriate spot, looks at whatever is relevant
 * (the user while they speak, parts the AI acts on, the current selection),
 * and moves with smooth, human-like motion. There is only one Spark.
 *
 * Behavior ported 1:1 where it applies to the desktop web (single local
 * user, camera = user head): standing placement in front of the camera,
 * critically damped standing motion, proximity step-forward/back with
 * deadzone + pitch-relief step-back, kneeling for
 * small low objects, focus-point arc transitions (Bézier + eased progress,
 * duration derived from rotation angle), contextual fixation-based idle head
 * motion, eye lasers while interacting (currently disabled —
 * `updateEyeLasers` draw is commented out), and the spinning `nimbus` crown
 * it wears while it works.
 *
 * Deliberate web deviations (desktop-only slice):
 * - Quest paths are not ported: multi-user centroid/billboard placement,
 *   `stand_here` fingertip overrides, and the transcript billboard.
 * - Unity's continuous `Mathf.PerlinNoise` drift is replaced by short,
 *   seeded head shifts between held fixations, with quiet micro-posture while
 *   holding eye contact. The cadence differs when Spark attends to the user
 *   versus a model.
 * - A desktop pointer is a head-only attention overlay: model hits use their
 *   true world point, empty canvas uses a virtual monitor plane, and personal
 *   space uses a plane 10 cm in front of the face. The local user's laser uses
 *   its true endpoint. None replaces the orchestrator's semantic target or
 *   drives Spark's body placement. Non-user looks
 *   occasionally check back with the user, then resolve the latest underlying
 *   target again. Live user/agent speech outranks every scene cue.
 * - Standing placement is one visibility-aware plan, not a five-second chase
 *   after the camera. A clear world-space spot is held until it is occluded or
 *   the user has meaningfully changed viewpoint/distance; only then does the
 *   camera-space search choose another in-frame slot. Scene depth and marked
 *   workflow/chat DOM surfaces feed the same candidate rejection pass.
 * - Appearance options (`SetAppearanceByName`) are not ported — the web
 *   ships the single mannequin head.
 */

// -- ISparkBehavior (port of `AI/Spark/ISparkBehavior.cs`) ------------------

export interface ISparkBehavior {
  readonly isVisible: boolean;
  /**
   * Raise the thinking crown. Named for Unity's `SparkBehavior.isThinking`
   * field, but that field is a misnomer in both clients: `UpdateActivityState`
   * assigns it `IsActivityActive`, so the crown is up for the whole turn —
   * thinking, tools, an open response, and the audio still playing after all
   * three have settled.
   */
  setThinking(thinking: boolean): void;
  setInteracting(interacting: boolean): void;
  /** Unity `CheckSpeakingParticipants`: follow the camera while the user talks. */
  setUserSpeaking(speaking: boolean): void;
  /** Keep conversational eye contact while Spark's own audio is playing. */
  setAgentSpeaking(speaking: boolean): void;
  /**
   * Unity `SetObjectOfInterest` — called when the AI acts on a part.
   *
   * `holdSeconds` makes the look TEMPORARY: Spark holds the node for that long
   * and then falls back to {@link lookAtUser}, which is this behaviour's own
   * resting gaze. Omit it (or pass <= 0) for the Unity default, a pin that
   * lasts until something else retargets the gaze — `look_at` is exactly that,
   * and giving it a hold would defeat the one tool whose purpose is to pin.
   *
   * The countdown runs on the FRAME clock rather than a `setTimeout`: there is
   * then no timer to cancel on dispose and none that can fire into a
   * torn-down viewport, and a glance lasts two seconds of VISIBLE time rather
   * than two seconds of wall clock elapsed while the tab was hidden.
   */
  setObjectOfInterest(node: Object3D | null, holdSeconds?: number): void;
  /** Unity `LookAtUser` — look back at the camera. */
  lookAtUser(): void;
  showSpark(): void;
  hideSpark(): void;
}

/** A painted DOM surface in viewport/client coordinates. */
export interface SparkScreenOccluderRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Motor-only pointer routing; it deliberately remains outside ISparkBehavior. */
export interface SparkPointerAttentionOptions {
  /** Skip the ordinary new-subject dwell, while retaining motor smoothing. */
  readonly acquireImmediately?: boolean;
  /** How this one acquired subject participates in social check-ins. */
  readonly checkIn?: "after-settle" | "before-target" | "none";
  /** Treat a captured cue as stable even while its motor point catches up. */
  readonly settleImmediately?: boolean;
}

/** Result of Spark's private-head raycast in the same world frame as scene picks. */
export interface SparkVisibleHeadHit {
  readonly distance: number;
  readonly worldPoint: Readonly<{ x: number; y: number; z: number }>;
}

/** Viewport-owned inputs which deliberately stay outside `ISparkBehavior`. */
export interface SparkBehaviorOptions {
  /**
   * Read the live glass boxes which can cover Spark in the composited page.
   * The runtime snapshots them once per visibility probe; it never imports Vue
   * or asks which workflow surface produced a rectangle.
   */
  readonly screenOccluders?: () => readonly SparkScreenOccluderRect[];
}

/**
 * Registration seam mirroring Unity: `SparkBehavior` registers itself with
 * the orchestrator (`IOC.RegisterFallback<ISparkBehavior>` +
 * `AIOrchestratorModel.RegisterSparkBehavior`). The runtime layer owns the
 * contract; `AIOrchestratorModel` implements it.
 */
export interface ISparkBehaviorHost {
  registerSparkBehavior(spark: ISparkBehavior): void;
  unregisterSparkBehavior(spark: ISparkBehavior): void;
}

export const SPARK_BEHAVIOR_HOST = Symbol.for("Campfire.SparkBehaviorHost");

// -- Prefab constants (SparkBehavior.prefab / head wrapper) ------------------

const HEAD_URL = publicUrl("avatars/head.glb");
// Same wrapper transform the multiuser head uses (`networkUsersRenderer.ts`).
//
// Spark's pivot is built three-natively (see `calculateStandingPosition`) and
// never goes through the Unity world conversion, so task 0004 did not move this
// frame the way it moved the multiuser one. The X sign is corrected anyway: the
// value was copied from `networkUsersRenderer` back when THAT frame was
// mirrored, so it was carrying a 1 mm left-right error into a frame that never
// had one. Both files are now proper right-handed, forward -Z, right +X — the
// shared value is correct for both.
// Neck → head pivot offset (prefab `Head` local y). Keep the prefab height as
// Spark's standing baseline even though the web rotation center is lifted
// below; otherwise raising the pivot would lower the root and move the name
// tag with it.
const HEAD_BASE_PIVOT_HEIGHT = 0.091;
// WEB DEVIATION (user direction 2026-09-04): idle pitch/tilt looked hinged at
// the bottom of the head. Lift the rotation center by 50% of the authored
// pivot height so it passes through the skull's visual center. Every child of
// the pivot is counter-offset by the same amount to preserve the resting pose.
const HEAD_ROTATION_CENTER_LIFT = HEAD_BASE_PIVOT_HEIGHT * 0.5;
const HEAD_ROTATION_PIVOT_HEIGHT = HEAD_BASE_PIVOT_HEIGHT + HEAD_ROTATION_CENTER_LIFT;
const HEAD_LOCAL_POSITION: readonly [number, number, number] = [
  0.001,
  -0.053 - HEAD_ROTATION_CENTER_LIFT,
  -0.062,
];
const HEAD_LOCAL_SCALE = 0.038;
const HEAD_LOCAL_YAW_RAD = Math.PI / 2; // head13 faces +X → face the pivot's -Z.
// Eye laser origins in head-pivot space (prefab eye laser locals, -Z face).
// Left unchanged by task 0004 on purpose: the pair is symmetric about X, so the
// X-sign correction applied to the head offset above maps this SET onto itself.
const EYE_OFFSETS: ReadonlyArray<readonly [number, number, number]> = [
  [-0.0303, 0.0103 - HEAD_ROTATION_CENTER_LIFT, -0.1086],
  [0.0303, 0.0103 - HEAD_ROTATION_CENTER_LIFT, -0.1086],
];
// Thinking indicator — the prefab `Head/Thinking` group. Its visible content is
// the `nimbus.fbx` crown, NOT the `Red Dot` sphere sitting beside it: that
// GameObject ships `m_IsActive: 0` and `SparkBehavior.Update` toggles only their
// shared parent (`thinkingIndicator.SetActive(isThinking)`), so the dot never
// renders on the desktop client. The web used to port the dot and not the
// crown, which is why Spark's thinking state did not match Unity's.
//
// `spark-nimbus.glb` is `nimbus.fbx` converted for the web: three coplanar
// ring/arc meshes (`nimbus_001` / `_002` / `_003` — the FBX spells them with
// DOTS, but three's `GLTFLoader` runs every node name through
// `PropertyBinding.sanitizeNodeName`, which strips the reserved characters
// `[].:/`, so `nimbus.001` would arrive as `nimbus001` and every lookup below
// would miss in silence) whose disc lies in their
// local XY with the thin axis on local Z, carrying the FBX's authored start
// phase about that axis. Vertices are pre-multiplied by 0.01 — the FBX declares
// `UnitScaleFactor 1.0` (centimetres) and Unity's importer bakes the
// centimetre→metre conversion into the mesh — so the asset is already in the
// metre space `NIMBUS_SCALE` below expects.
const NIMBUS_URL = publicUrl("avatars/spark-nimbus.glb");
// `Crown.mat` `_BaseMap` — a 128×128 sheet of binary digits, white on
// transparent, used purely as a mask for the tint below.
const NIMBUS_TEXTURE_URL = publicUrl("avatars/spark-nimbus.png");

// Prefab `Head/Thinking/nimbus` local transform.
//
// Unity's `Head` frame carries head13's NATIVE axes (+X is the face direction),
// because the 90° yaw that turns Spark to face the user sits one level up on
// `Neck`. The web puts that same yaw on the head MESH instead
// (`HEAD_LOCAL_YAW_RAD`), so the pivot frame here is the un-yawed one and a
// Head-frame vector (x, y, z) reads as (z, y, -x) in it.
//
// The web head also hangs at `HEAD_LOCAL_POSITION` rather than at the pivot
// origin the way Unity's `SmartHead` does, so that offset is added back: what
// has to match Unity is where the crown sits on the SKULL, not on the pivot.
// Unity's value is (-0.016000032, 0.05799999, 0) — 5.8 cm up and 1.6 cm back,
// which lands the band just above the head's widest point.
const NIMBUS_LOCAL_POSITION: readonly [number, number, number] = [
  HEAD_LOCAL_POSITION[0] + 0, //             Unity Head-local  z
  HEAD_LOCAL_POSITION[1] + 0.05799999, //    Unity Head-local  y
  HEAD_LOCAL_POSITION[2] + 0.016000032, //   Unity Head-local -x
];
// Prefab rotation is -90° about the Head frame's X, which stands the rings' XY
// disc up into the horizontal plane (their thin local Z becomes vertical).
// Composed with that frame's own 90° yaw as `YXZ`, so the product is
// R_y(90°)·R_x(-90°); the yaw only picks the crown's starting azimuth, which
// the spin below sweeps continuously.
const NIMBUS_ROTATION = new Euler(-Math.PI / 2, Math.PI / 2, 0, "YXZ");
// Prefab `m_LocalScale` (10, 10, 20). The disc is local XY, so 10 takes the
// exported 0.0105 m rings out to a 0.105 m radius — the head's own radius at
// that height, i.e. a band around the skull rather than a halo floating over
// it. The thin local Z takes 20, doubling each band's vertical depth.
const NIMBUS_SCALE: readonly [number, number, number] = [10, 10, 20];
// `Crown.mat` `_BaseMap` tiling (0.5, 1) — half a sheet of digits per wrap.
const NIMBUS_MAP_REPEAT: readonly [number, number] = [0.5, 1];
// `Crown.mat` `_BaseColor`, transcribed verbatim. A Unity `.mat` value, so it is
// display-authored — Population B in `runtime/engine/COLOR_PIPELINE.md` —
// and is declared as the sRGB input it is: decoded here, encoded back by the
// output transfer, so it displays as #F84536, the Campfire accent. (It went
// through the numeric constructor un-decoded while the app skipped that
// transfer; that mode is gone.)
const NIMBUS_COLOR = new Color().setRGB(0.972549, 0.27058825, 0.21176471, SRGBColorSpace);
// Per-ring spin, °/s about the ring's own axis. Signs are DIRECTIONS.
//
// The MAGNITUDES are the prefab's: the three `VLB_Samples.Rotater` components
// it adds to the ring children carry `EulerSpeed` (0,0,10), (0,0,10), (0,0,20).
//
// The DIRECTIONS are not readable from that data, and reproducing upstream's
// arithmetic does not yield them. `Rotater` writes `transform.rotation` — the
// WORLD rotation — and adds to its Z euler; with the crown horizontal that ZXY
// decomposition sits exactly on Unity's south-pole singularity (x = -90°),
// where `eulerAngles` discards Z and folds everything into Y. Worked through,
// the locked round trip advances every ring the SAME way, which is not what the
// desktop client shows: the rings visibly counter-rotate. What actually drives
// that is the head pitching Spark off the pole, putting the decomposition in
// its unstable band where the recovered Z — and therefore each ring's effective
// direction — depends on that ring's own start phase.
//
// So this ports the OBSERVED behaviour rather than the numerical artifact that
// produces it: fixed, stable, per-ring directions. Do not "correct" these back
// to three positive values to match the serialized `EulerSpeed` — that is the
// version that reads as one rigid piece. Which ring turns which way is a
// judgement (the prefab addresses the three by fileID, which cannot be mapped
// to node names outside the editor); flipping a sign here is the whole change.
const NIMBUS_SPIN_DEG_PER_S: Readonly<Record<string, number>> = {
  nimbus_001: 10,
  nimbus_002: -20,
  nimbus_003: -10,
};
// Name tag (`AI/Spark/NameTag.prefab` instance on the Spark GameObject):
// "Spark" pill with the spark-300 picture disc, cyan-blue accent
// (SparkBehavior.prefab override r0 g0.5315 b1), hung below the head.
const NAME_TAG_URL = publicUrl("avatars/spark.png");
const NAME_TAG_COLOR = "rgb(0, 136, 255)";
const NAME_TAG_OFFSET_Y = -0.11;

// -- Behavior constants (serialized defaults on the prefab component) --------

// Serialized prefab overrides win over the C# field defaults.
const SPARK_HEIGHT_FALLBACK = 1.7;
const SPARK_DISTANCE_FROM_USER = 2.0;
// The project floor is world Y=0. A clear screen/depth sample below it is not
// a valid place for a participant, even when camera pitch makes it look like
// the nearest escape. This is the standing/root height, not the skull centre.
const SPARK_MIN_STANDING_Y = 0.3;
// ⚠ WEB DEVIATION: the prefab serializes 0.2; this is deliberately 3x it
// (user direction 2026-09-01, tuned over two passes — 0.2 -> 0.4 -> 0.6).
// Spark stands well off the camera's centre line so it clears the middle of
// the viewport, where the model being discussed usually is. Restore 0.2 to
// match the desktop client.
//
// Bounds, so the next nudge does not need re-deriving: at the default 60 deg
// vertical FOV on a 16:9 viewport the frustum's half-width at Spark's 2 m
// depth is ~2.05 m, so this offset is ~29% of the way to the left edge and
// ~17 deg off centre. There is room to roughly 1.6 before the head starts
// leaving frame. The HOME equation remains deliberately unclamped; the
// visibility-slot layer below supplies an in-frame alternate on a narrow view.
const SPARK_LATERAL_OFFSET_FROM_USER = -0.6;
const SPARK_HEIGHT_OFFSET = 0.0;
const POSITION_SMOOTHING_TIME_S = 0.25;
const BASE_POSITION_SMOOTHING_TIME_S = 0.5;
const SECONDARY_OFFSET_SMOOTHING_TIME_S = 0.25;

// A standing plan is a WORLD spot. Camera motion below these thresholds is
// ordinary looking/head motion and must not tow Spark around the scene. The
// dwell keeps a quick orbit correction from being mistaken for walking to the
// other side of the model; near/far bounds still catch meaningful translation
// even when the camera ends with a similar forward vector.
const STANDING_REPLAN_TRANSLATION_M = 0.8;
const STANDING_REPLAN_PROXIMITY_CAMERA_TRANSLATION_M = 0.1;
const STANDING_REPLAN_NEAR_DISTANCE_M = 1.1;
const STANDING_REPLAN_FAR_DISTANCE_M = 3.4;
const STANDING_REPLAN_FORWARD_ANGLE_DEG = 55;
const STANDING_REPLAN_FORWARD_DOT = Math.cos(
  STANDING_REPLAN_FORWARD_ANGLE_DEG * Math.PI / 180,
);
const STANDING_REPLAN_AROUND_ANCHOR_ANGLE_DEG = 70;
const STANDING_REPLAN_AROUND_ANCHOR_DOT = Math.cos(
  STANDING_REPLAN_AROUND_ANCHOR_ANGLE_DEG * Math.PI / 180,
);
const STANDING_CAMERA_SETTLE_S = 0.4;
// A failed social/viewpoint search is latched to the viewpoint that produced
// it. Do not wake up every probe merely because the held fallback remains a
// few centimetres beyond a near/far boundary; retry only after the user has
// actually changed the view enough that a different answer may exist.
const STANDING_FAILED_VIEWPOINT_TRANSLATION_M = 0.25;
const STANDING_FAILED_VIEWPOINT_ANGLE_DEG = 15;
const STANDING_FAILED_VIEWPOINT_FORWARD_DOT = Math.cos(
  STANDING_FAILED_VIEWPOINT_ANGLE_DEG * Math.PI / 180,
);
// These are numerical-noise floors, not velocity gates. Any perceptible
// change resets the settle dwell so a very slow continuous orbit cannot be
// mistaken for a sequence of stationary viewpoints and relocate mid-gesture.
const STANDING_CAMERA_MOTION_TRANSLATION_M = 0.0001;
const STANDING_CAMERA_MOTION_ANGLE_DEG = 0.01;
const STANDING_CAMERA_MOTION_FORWARD_DOT = Math.cos(
  STANDING_CAMERA_MOTION_ANGLE_DEG * Math.PI / 180,
);
const STANDING_CAMERA_MOTION_PROJECTION_EPSILON = 0.001;

// Visibility-aware standing placement (web-only). The social home remains the
// authored 2 m / -0.6 m pose above. Alternates are screen-space anchors so the
// same search works with narrow canvases and the camera's asymmetric
// `setViewOffset` projection; each is reconstructed at the requested forward
// depth and rejected unless the whole sampled head envelope stays in frame.
const VISIBILITY_PROBE_INTERVAL_S = 0.25;
const VISIBILITY_BLOCKED_SAMPLE_COUNT = 2;
const VISIBILITY_RELOCATION_LOCK_S = 1.25;
const VISIBILITY_FAILED_SEARCH_RETRY_S = 2.5;
const VISIBILITY_DESTINATION_SETTLE_DISTANCE_M = 0.05;
const VISIBILITY_OCCLUSION_EPSILON_M = 0.002;
const VISIBILITY_SAFE_NDC = 0.9;
// `head13` is asymmetric about the authored gaze/standing centre. At the
// shipped 0.038 scale its mesh top is 0.12 m above this point and the shader's
// fully opaque jaw begins 0.064 m below it. The much wider mesh below that is
// the deliberately faded bust/shoulder slab, so it must never make a visible
// skull count as hidden (or make a candidate reserve invisible body room).
// Scene rays sit inside the actual front-facing skull rather than on its
// projected screen AABB. Sampling the outer box edge would mostly test
// clear air and let real lower-face coverage hide among false-clear points.
const VISIBILITY_HEAD_SIDE_SAMPLE_M = 0.067;
const VISIBILITY_HEAD_CROWN_SAMPLE_M = 0.108;
const VISIBILITY_HEAD_CHIN_SAMPLE_M = 0.055;
const VISIBILITY_HEAD_JAW_SAMPLE_X_M = 0.05;
const VISIBILITY_HEAD_JAW_SAMPLE_Y_M = 0.035;
// Screen/UI candidates reserve the largest projected opaque-skull footprint
// observed over Spark's supported yaw/pitch range. This is still head-only —
// the faded bust is excluded — but a later pointer glance or user check-in
// cannot turn a previously-clear standing point into a new panel collision.
const VISIBILITY_SKULL_POSE_RESERVE_HALF_WIDTH_M = 0.2;
const VISIBILITY_SKULL_POSE_RESERVE_TOP_M = 0.205;
const VISIBILITY_SKULL_POSE_RESERVE_BOTTOM_M = 0.195;
// Native `head13` AABB after discarding every vertex below the Spark shader's
// fully-opaque jaw cutoff (object Y < -0.3). Projecting this oriented box with
// the live neck/head rotations keeps the DOM/frame envelope on the skull even
// at a -45° look-down; a fixed camera-up rectangle either misses the pitched
// chin or reintroduces empty/hidden bust space in the neutral pose.
const VISIBILITY_SKULL_NATIVE_MIN: readonly [number, number, number] = [
  -2.5912799835,
  -0.3,
  -2.0037009716,
];
const VISIBILITY_SKULL_NATIVE_MAX: readonly [number, number, number] = [
  3.0501520634,
  4.5645618439,
  2.0192840099,
];
// Interior sample centre stays on the authored standing/gaze centre rather
// than the OBB's geometric centre (which is 2.8 cm higher). It passes through
// the face; x/z use the opaque skull box centre so pitch rotates it with the
// rendered mesh instead of around the old root-plane approximation.
const VISIBILITY_SKULL_SAMPLE_CENTER_LOCAL: readonly [number, number, number] = [
  HEAD_LOCAL_POSITION[0] + 0.0077915192 * HEAD_LOCAL_SCALE,
  HEAD_BASE_PIVOT_HEIGHT - HEAD_ROTATION_PIVOT_HEIGHT,
  HEAD_LOCAL_POSITION[2] - 0.2294360399 * HEAD_LOCAL_SCALE,
];
const VISIBILITY_SKULL_SAMPLE_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [VISIBILITY_HEAD_SIDE_SAMPLE_M, 0],
  [-VISIBILITY_HEAD_SIDE_SAMPLE_M, 0],
  [0, VISIBILITY_HEAD_CROWN_SAMPLE_M],
  [0, -VISIBILITY_HEAD_CHIN_SAMPLE_M],
  [-VISIBILITY_HEAD_JAW_SAMPLE_X_M, -VISIBILITY_HEAD_JAW_SAMPLE_Y_M],
  [VISIBILITY_HEAD_JAW_SAMPLE_X_M, -VISIBILITY_HEAD_JAW_SAMPLE_Y_M],
];
const VISIBILITY_SKULL_RESERVED_SAMPLE_OFFSETS: ReadonlyArray<
  readonly [number, number]
> = [
  [0, 0],
  [0.17, 0],
  [-0.17, 0],
  [0, 0.145],
  [0, -0.13],
  [-0.13, -0.09],
  [0.13, -0.09],
];
const VISIBILITY_SAMPLE_COUNT = 7;
// Permit one small edge overlap, but require the centre and almost the entire
// visible skull. The two jaw probes keep a seat/ledge hiding the lower face
// from passing merely because the cardinal centre/top/side probes are clear.
const VISIBILITY_ACCEPTABLE_SAMPLE_COUNT = 6;
const VISIBILITY_SCREEN_GAP_PX = 12;
const VISIBILITY_SCREEN_PLACEMENT_EPSILON_PX = 1;
const VISIBILITY_SEARCH_SLOT_DEDUP_NDC = 0.006;
const VISIBILITY_MAX_SCREEN_OCCLUDERS = 8;
const VISIBILITY_PANEL_EDGE_CANDIDATES = 4;
const VISIBILITY_LOCAL_SEARCH_RING_COUNT = 3;
const VISIBILITY_LOCAL_SEARCH_SUBDIVISIONS = 4;
const VISIBILITY_LOCAL_SEARCH_MIN_STEP_NDC = 0.075;
const VISIBILITY_LOCAL_SEARCH_MAX_STEP_NDC = 0.12;
const VISIBILITY_LOCAL_SEARCH_DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [Math.SQRT1_2, Math.SQRT1_2],
  [1, 0],
  [Math.SQRT1_2, -Math.SQRT1_2],
  [0, -1],
  [-Math.SQRT1_2, -Math.SQRT1_2],
  [-1, 0],
  [-Math.SQRT1_2, Math.SQRT1_2],
];
const HOME_VISIBILITY_SLOT_INDEX = 0;

interface SparkVisibilitySlot {
  /** `null` preserves the exact social-home equation above. */
  ndcX: number | null;
  /** `null` preserves the social home's projected height. */
  ndcY: number | null;
  forwardDepthM: number;
}

interface SparkVisibilityFinalist {
  slotIndex: number;
  cost: number;
  readonly basePosition: Vector3;
}

interface MutableSparkScreenRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Ordered broadly from Spark's normal left-side home outward, then across the
// user, with two closer fallbacks for a deep/wide occluder and three vertical
// anchors for steep camera pitch. The actual winner is costed in world space,
// so this order only breaks exact ties.
const SPARK_VISIBILITY_SLOTS: readonly SparkVisibilitySlot[] = [
  { ndcX: null, ndcY: null, forwardDepthM: SPARK_DISTANCE_FROM_USER },
  { ndcX: -0.5, ndcY: null, forwardDepthM: 2.0 },
  { ndcX: -0.72, ndcY: null, forwardDepthM: 2.0 },
  { ndcX: -0.08, ndcY: null, forwardDepthM: 2.0 },
  { ndcX: 0.3, ndcY: null, forwardDepthM: 2.0 },
  { ndcX: 0.55, ndcY: null, forwardDepthM: 2.0 },
  { ndcX: 0.75, ndcY: null, forwardDepthM: 2.0 },
  { ndcX: -0.63, ndcY: null, forwardDepthM: 1.5 },
  { ndcX: 0.63, ndcY: null, forwardDepthM: 1.5 },
  { ndcX: -0.35, ndcY: 0.5, forwardDepthM: 2.0 },
  { ndcX: -0.35, ndcY: 0, forwardDepthM: 2.0 },
  { ndcX: -0.35, ndcY: -0.5, forwardDepthM: 2.0 },
];
const VISIBILITY_MAX_SEARCH_SLOTS =
  SPARK_VISIBILITY_SLOTS.length +
  VISIBILITY_LOCAL_SEARCH_RING_COUNT *
    VISIBILITY_LOCAL_SEARCH_SUBDIVISIONS *
    VISIBILITY_LOCAL_SEARCH_DIRECTIONS.length +
  VISIBILITY_MAX_SCREEN_OCCLUDERS * VISIBILITY_PANEL_EDGE_CANDIDATES;
const IDLE_ROTATION_SHARPNESS = 6.0;
const PERSONAL_SPACE_ROTATION_SHARPNESS = IDLE_ROTATION_SHARPNESS * 2;
const HEAD_INTRUSION_ROTATION_SHARPNESS = 10.0;
const IDLE_RANDOM_SEED_RANGE = 0x1_0000_0000;
const POINTER_ATTENTION_SETTLE_S = 0.35;
// A person does not redirect their whole head toward every object the cursor
// crosses. Require a brief, stable new subject before committing, then let a
// slower critically damped point carry the head there. Motion within the same
// subject remains live, so this filters target-hopping without making a drag
// across one model feel disconnected.
const POINTER_ATTENTION_ACQUISITION_DELAY_S = 0.1;
const POINTER_ATTENTION_SMOOTHING_TIME_S = 0.2;
const PERSONAL_SPACE_ATTENTION_SMOOTHING_TIME_S =
  POINTER_ATTENTION_SMOOTHING_TIME_S * 0.5;
const POINTER_ATTENTION_GAP_GRACE_S = 0.2;
const SPARK_HEAD_POINTER_SUBJECT = "local:spark-head";
const SPARK_HEAD_OPAQUE_LOCAL_Y_MIN = -0.3;
const SPARK_HEAD_HIT_SCENE_EPSILON_M = 0.002;
const SPARK_PERSONAL_SPACE_SKULL_SCALE = 3.6;
const SPARK_PERSONAL_SPACE_FACE_CLEARANCE_M = 0.1;
const SPARK_VISIBLE_FACE_FORWARD_EXTENT_M = Math.abs(
  HEAD_LOCAL_POSITION[2] -
    VISIBILITY_SKULL_NATIVE_MAX[0] * HEAD_LOCAL_SCALE,
);
const SPARK_PERSONAL_SPACE_LEAN_M = 0.2;
const SPARK_PERSONAL_SPACE_MAX_VERTICAL_DODGE_M =
  SPARK_PERSONAL_SPACE_LEAN_M * 0.5;
// Personal-space avoidance is a small repulsion motor, not two poses. Its
// velocity has inertia, so a cursor crossing the face decelerates and steers
// Spark around continuously instead of flipping a left/right target.
const SPARK_PERSONAL_SPACE_DODGE_DEAD_ZONE_M = 0.015;
const SPARK_PERSONAL_SPACE_DODGE_MAX_SPEED_M_S = 0.9;
const SPARK_PERSONAL_SPACE_DODGE_POSITION_GAIN_S = 8;
const SPARK_PERSONAL_SPACE_DODGE_STEERING_SHARPNESS = 8;
const SPARK_PERSONAL_SPACE_LEAN_SMOOTHING_TIME_S =
  POINTER_ATTENTION_SMOOTHING_TIME_S;
const SPARK_PERSONAL_SPACE_SUBJECT = "local:spark-personal-space";
const TARGET_RADIUS_FRACTION = 0.5;
const SCREEN_CENTER_FRACTION = 0.5;
const MIN_TARGET_DISTANCE_M = 0.001;
const DIRECTION_EPSILON_SQ = 1e-8;
/**
 * Squared speed (m/s) above which Spark counts as TRAVELLING for render on
 * demand, and draws at the full rate instead of 30 fps. 2 cm/s: the smooth
 * damp's tail runs on for seconds at speeds no one can see.
 */
const K_RENDER_MOVING_SPEED_SQ = 0.02 * 0.02;
/**
 * Margin (m) round Spark's bounds for the on-screen test that gates its render
 * sources — enough that a sliver at the frame edge still counts as seen.
 */
const K_RENDER_BOUNDS_MARGIN_M = 0.05;
const HEAD_ROTATION_SPEED_DEG_S = 20.0;
const MIN_ROTATION_DURATION_S = 1.0;
const MAX_ROTATION_DURATION_S = 3.0;
const ARC_HEIGHT_MULTIPLIER = 0.3;
const MAX_COMFORTABLE_PITCH_DOWN_DEG = 10.0;
const PITCH_HYSTERESIS_DEG = 2.0;
const PITCH_BACK_STEP_MULTIPLIER = 0.1;
const PROXIMITY_THRESHOLD_M = 1.0;
const PROXIMITY_DEADZONE_M = 0.05;
const FORWARD_MOVEMENT_AMOUNT_M = 0.3;
const BACKWARD_MOVEMENT_AMOUNT_M = 0.3;
const PROXIMITY_RESPONSE_SCALE = 0.5;
const HOLD_OFFSET_INSIDE_COMFORT_ZONE = true;
const ENABLE_KNEELING = false; // Prefab override (kneeling shipped disabled).
const SMALL_OBJECT_THRESHOLD_M = 0.3;
const KNEELING_DISTANCE_THRESHOLD_M = 1.0;
const KNEELING_MAX_HEIGHT_M = 1.2;
const KNEELING_HEIGHT_REDUCTION_M = 0.5;
const KNEELING_DELAY_S = 1.0;
const SECONDARY_BODY_ADJUSTMENT_DELAY_S = 0.3;
const SECONDARY_BODY_ADJUSTMENT_SPEED = 10.0;
const EASING_STRENGTH = 1.5;
const OBJECT_MOVEMENT_THRESHOLD_M = 0.01;
const PITCH_CLAMP_MIN_DEG = -45.0;
const PITCH_CLAMP_MAX_DEG = 30.0;

const DEG2RAD = Math.PI / 180;

export class SparkBehavior implements ISparkBehavior {
  private readonly m_root = new Group();
  private readonly m_neck = new Group();
  private readonly m_headPivot = new Group();
  private readonly m_focusPoint = new Object3D();
  private readonly m_glowMaterial = createAvatarGlowMaterial("spark");
  private m_headModel: Object3D | null = null;
  private readonly m_headPointerRaycaster = new Raycaster();
  private readonly m_headPointerIntersections: Intersection<Object3D>[] = [];
  private readonly m_headPointerLocalHit = new Vector3();
  // `Crown.mat` is URP/Lit, but the crown is chrome rather than scene content
  // and its tint is display-authored, so it is UNLIT here: a lit material would
  // land at 20–70 % of the authored color under the viewport's linear output
  // (`runtime/engine/COLOR_PIPELINE.md`), which is the one thing this port has
  // to get right. `depthWrite` off + back-face culling are the material's own
  // `_ZWrite: 0` / `_Cull: 2`; depth TEST stays on, so the head occludes the
  // half of each band passing behind it and the crown reads as orbiting.
  private readonly m_nimbusMaterial = new MeshBasicMaterial({
    color: NIMBUS_COLOR,
    transparent: true,
    depthWrite: false,
    side: FrontSide,
  });
  private m_nimbusTexture: Texture | null = null;
  private m_thinkingIndicator: Group | null = null;
  private readonly m_nimbusRings: Array<{
    node: Object3D;
    baseAngleRad: number;
    speedRadPerS: number;
  }> = [];
  private m_nimbusSpinS = 0;
  private m_nameTag: Sprite | null = null;
  // Hidden until a host says otherwise (user direction, 2026-09-17). The
  // resting state matters because `buildAttachments` reads it when the tag
  // image finishes loading, which can be after mount — a host that forgets to
  // call `setNameTagVisible` gets no label rather than a label nobody asked for.
  private m_isNameTagVisible = false;
  private readonly m_eyeLasers: Line[] = [];
  /** Scratch for `isSeenOnScreen`, which runs every frame. */
  private readonly m_renderBounds = new Box3();
  private readonly m_renderFrustum = new Frustum();
  private readonly m_renderViewProjection = new Matrix4();
  private readonly m_laserMaterial = new LineBasicMaterial({
    color: new Color().setRGB(0.3, 0.85, 1, SRGBColorSpace),
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    blending: AdditiveBlending,
  });

  private m_isVisible = false;
  private m_isThinking = false;
  private m_isInteracting = false;
  private m_isUserSpeaking = false;
  private m_isAgentSpeaking = false;
  private m_disposed = false;
  private m_timeS = 0;
  private m_pickingService: PickingService | null = null;
  private readonly m_idleMotion: NaturalIdleHeadMotion;
  private readonly m_userCheckIn: NaturalUserCheckIn;
  private readonly m_headIntrusionReaction: NaturalHeadIntrusionReaction;
  private m_isHeadIntrusionLookingAtUser = false;

  private m_objectOfInterest: Object3D | null = null;
  // A temporary look and when it lapses (see `setObjectOfInterest`). The NODE
  // is kept, not just the deadline, so `expireTemporaryGaze` can tell whether
  // the gaze it is about to release is still the one the hold was taken for.
  private m_gazeHoldNode: Object3D | null = null;
  private m_gazeHoldExpiresAtS = 0;
  private m_currentObjectOfInterest: Object3D | null = null;
  private m_pendingSemanticHeadTarget: Object3D | null = null;

  // Head attention is layered independently from the semantic target above:
  // conversation > check-in > local pointing > orchestrator target. Keeping
  // this seam separate prevents a cursor from making Spark step or kneel
  // toward a part.
  private readonly m_pointerAttentionTarget = new Object3D();
  private readonly m_pointerAttentionGoal = new Vector3();
  private readonly m_pointerAttentionVelocity = new Vector3();
  private m_pointerAttentionSubjectKey: string | null = null;
  private readonly m_pendingPointerAttentionPoint = new Vector3();
  private m_pendingPointerAttentionSubjectKey: string | null = null;
  private m_pendingPointerAttentionSinceS = 0;
  private m_pendingPointerCheckIn: NonNullable<
    SparkPointerAttentionOptions["checkIn"]
  > = "after-settle";
  private m_pendingPointerSettleImmediately = false;
  private m_hasPointerAttention = false;
  private m_pointerAttentionUpdatedAtS = -Infinity;
  private m_pointerAttentionReleaseAtS = Infinity;
  // Armed once per pointer-subject acquisition, then consumed only after the
  // tracked point has settled. Same-subject motion never re-arms it.
  private m_pointerImmediateCheckInPending = false;
  private m_pointerBeforeTargetCheckInPending = false;
  private m_checkInSubject: Object3D | null = null;
  private m_isCheckingInWithUser = false;
  private m_currentHeadTarget: Object3D | null = null;
  private readonly m_lastHeadTargetPosition = new Vector3();
  private m_isHeadTargetMoving = false;

  // Focus-point arc transition (LeanTween port).
  private m_isTransitioning = false;
  private m_transitionT = 0;
  private m_transitionDuration = 0;
  private readonly m_transitionStart = new Vector3();
  private readonly m_transitionMid = new Vector3();
  private readonly m_transitionEnd = new Vector3();

  // Standing placement state.
  private m_hasCachedStandingPosition = false;
  private readonly m_baseSparkPosition = new Vector3();
  private readonly m_cachedBaseStandingPosition = new Vector3();
  private readonly m_smoothedBaseStandingPosition = new Vector3();
  private readonly m_currentSecondaryBodyOffset = new Vector3();
  // A pointer inside the projected personal-space halo makes the upper body
  // dodge locally while the root remains on its committed world-space
  // standing spot. Keeping this out of body/visibility offsets prevents an
  // expressive retreat from becoming a relocation away from the user.
  private readonly m_headPointerLeanOffset = new Vector3();
  private readonly m_headPointerLeanTarget = new Vector3();
  private readonly m_headPointerLeanVelocity = new Vector3();
  private readonly m_headPointerLeanRootRotation = new Quaternion();
  private readonly m_headPointerLeanRootInverse = new Quaternion();
  private readonly m_headPointerCameraRight = new Vector3();
  private readonly m_headPointerCameraUp = new Vector3();
  private readonly m_headPointerWorldOffset = new Vector3();
  private readonly m_headPointerAvoidancePoint = new Vector3();
  private m_hasHeadPointerAvoidancePoint = false;
  private m_headPointerDodgeLateralM = 0;
  private m_headPointerDodgeVelocityM_S = 0;
  private m_headPointerDodgeBias: -1 | 1 = 1;
  private m_headPointerProximity01 = 0;
  private m_isPersonalSpacePointerActive = false;
  private m_isHeadPointerHoverActive = false;
  private readonly m_sparkPositionVelocity = new Vector3();
  private readonly m_basePositionVelocity = new Vector3();
  private readonly m_secondaryOffsetVelocity = new Vector3();
  private m_cachedDynamicBaseHeight = SPARK_HEIGHT_FALLBACK;
  private m_currentEffectiveHeight = SPARK_HEIGHT_FALLBACK;
  private m_hasStandingPlanReference = false;
  private readonly m_standingPlanCameraPosition = new Vector3();
  private readonly m_standingPlanCameraForward = new Vector3();
  private readonly m_standingPlanCameraFromAnchor = new Vector3();
  private m_hasFailedStandingViewpoint = false;
  private readonly m_failedStandingViewpointCameraPosition = new Vector3();
  private readonly m_failedStandingViewpointCameraForward = new Vector3();
  private readonly m_failedStandingViewpointProjection = new Float32Array(16);
  private m_hasStandingCameraMotionSample = false;
  private m_standingCameraLastMotionS = -Infinity;
  private readonly m_standingLastCameraPosition = new Vector3();
  private readonly m_standingLastCameraForward = new Vector3();
  private readonly m_standingLastCameraProjection = new Float32Array(16);

  // A visibility slot is evaluated camera-relative only when a standing plan
  // is made. The accepted target is cached in world space above and held; the
  // slot is retained solely so an occlusion/viewpoint replan can compare the
  // current side with new candidates. Scratch objects are retained because the
  // normal 4 Hz probe is on the render loop and CAD raycasts are expensive.
  private m_visibilitySlotIndex = HOME_VISIBILITY_SLOT_INDEX;
  private readonly m_visibilityActiveSlot: SparkVisibilitySlot = {
    ndcX: null,
    ndcY: null,
    forwardDepthM: SPARK_DISTANCE_FROM_USER,
  };
  private readonly m_visibilitySearchSlots: SparkVisibilitySlot[] = Array.from(
    { length: VISIBILITY_MAX_SEARCH_SLOTS },
    () => ({ ndcX: null, ndcY: null, forwardDepthM: SPARK_DISTANCE_FROM_USER }),
  );
  private m_visibilitySearchSlotCount = SPARK_VISIBILITY_SLOTS.length;
  private m_nextVisibilityProbeTimeS = 0;
  private m_visibilityBlockedSamples = 0;
  private m_visibilityDestinationBlockedSamples = 0;
  private m_visibilityEdgeProbeIndex = 0;
  private m_visibilityRelocationLockedUntilS = -Infinity;
  private m_visibilitySearchFoundImprovement = false;
  private m_isScreenOcclusionSuppressed = false;
  private readonly m_visibilityVerticalOffsetTarget = new Vector3();
  private readonly m_smoothedStandingHeight = new Vector3(
    0,
    SPARK_HEIGHT_FALLBACK,
    0,
  );
  private readonly m_standingHeightTarget = new Vector3(
    0,
    SPARK_HEIGHT_FALLBACK,
    0,
  );
  private readonly m_standingHeightVelocity = new Vector3();
  private readonly m_visibilityRay = new Ray();
  private readonly m_visibilityCameraPosition = new Vector3();
  private readonly m_visibilityForward = new Vector3();
  private readonly m_visibilitySocialRight = new Vector3();
  private readonly m_visibilityCameraRight = new Vector3();
  private readonly m_visibilityCameraUp = new Vector3();
  private readonly m_visibilityHomeBase = new Vector3();
  private readonly m_visibilityCurrentBase = new Vector3();
  private readonly m_visibilityCandidateBase = new Vector3();
  private readonly m_visibilityHeadCenter = new Vector3();
  private readonly m_visibilityEnvelopeRoot = new Vector3();
  private readonly m_visibilitySamplePoint = new Vector3();
  private readonly m_visibilityProjection = new Vector3();
  private readonly m_visibilityUnprojectedPoint = new Vector3();
  private readonly m_visibilityRayDirection = new Vector3();
  private readonly m_visibilityDisplacement = new Vector3();
  private m_visibilityCanvasLeft = 0;
  private m_visibilityCanvasTop = 0;
  private m_visibilityCanvasWidth = 0;
  private m_visibilityCanvasHeight = 0;
  private m_visibilityHeadScreenLeft = 0;
  private m_visibilityHeadScreenTop = 0;
  private m_visibilityHeadScreenRight = 0;
  private m_visibilityHeadScreenBottom = 0;
  private m_visibilityHeadNdcLeft = 0;
  private m_visibilityHeadNdcTop = 0;
  private m_visibilityHeadNdcRight = 0;
  private m_visibilityHeadNdcBottom = 0;
  private m_visibilityHeadEnvelopeProjectable = false;
  private m_visibilityScreenOccluderCount = 0;
  private readonly m_visibilityScreenOccluders: MutableSparkScreenRect[] = Array.from(
    { length: VISIBILITY_MAX_SCREEN_OCCLUDERS },
    () => ({ x: 0, y: 0, width: 0, height: 0 }),
  );
  private readonly m_visibilityFinalists: SparkVisibilityFinalist[] = Array.from(
    { length: VISIBILITY_MAX_SEARCH_SLOTS },
    () => ({ slotIndex: -1, cost: Infinity, basePosition: new Vector3() }),
  );

  private readonly m_isDepthOccludingSceneNode = (node: Object3D): boolean => {
    const picker = this.m_pickingService;
    if (!picker || picker.resolveSnapshotId(node) === null) return false;

    // The picker gives us the actual source mesh for ordinary and batched
    // snapshots. If an integration supplies only a snapshot proxy, treat it
    // conservatively as solid; otherwise inspect the material that decides
    // whether this surface can actually hide Spark in the depth buffer.
    const material = (node as Object3D & { material?: Material | Material[] }).material;
    if (!material) return true;
    return Array.isArray(material)
      ? material.some(isDepthOccludingMaterial)
      : isDepthOccludingMaterial(material);
  };

  // Secondary-adjustment / kneeling timing.
  private m_lastTransitionStartTimeS = 0;
  private m_shouldDelaySecondaryBodyAdjustment = false;
  private m_lastKneelingTriggerTimeS = 0;
  private m_shouldDelayKneeling = false;
  private m_wasKneelingConditionMet = false;

  // Bounds-size cache (Unity re-traverses renderers every frame; web
  // models can be huge, so recompute at most twice a second per target).
  private m_sizeCacheTarget: Object3D | null = null;
  private m_sizeCacheValue = 0;
  private m_sizeCacheTimeS = -1;

  private readonly m_offFns: Array<() => void> = [];

  constructor(
    private readonly m_engine: ThreeEngine,
    private readonly m_camera: PerspectiveCamera,
    private readonly m_options: SparkBehaviorOptions = {},
  ) {
    this.m_root.name = "sparkRoot";
    this.m_root.visible = false;
    this.m_neck.name = "spark.neck";
    this.m_headPivot.name = "spark.head";
    this.m_headPivot.position.set(0, HEAD_ROTATION_PIVOT_HEIGHT, 0);
    this.m_neck.add(this.m_headPivot);
    this.m_root.add(this.m_neck);
    this.m_focusPoint.name = "spark.focusPoint";
    this.m_pointerAttentionTarget.name = "spark.pointerAttention";
    const idleSeed = Math.floor(Math.random() * IDLE_RANDOM_SEED_RANGE);
    this.m_idleMotion = new NaturalIdleHeadMotion(
      idleSeed,
      () => this.readSharedAttentionCue(),
    );
    this.m_userCheckIn = new NaturalUserCheckIn(idleSeed);
    this.m_headIntrusionReaction = new NaturalHeadIntrusionReaction(idleSeed);
  }

  mount(): void {
    // ROOM space, not the stage. Spark's whole placement is world-space
    // arithmetic — `calculateStandingPosition` reads `camera.getWorldPosition()`
    // and `m_focusPoint` is fed from `node.getWorldPosition()` — while the
    // writes go to `.position`, which is LOCAL. Parented to `engine.root` that
    // is correct only while the stage sits at the origin, and console placement
    // moves the stage (see `runtime/xr/stageSpace.ts`); Spark would then stand
    // a full stage-offset away from the wearer it is meant to accompany.
    // Under `scene` (always identity) the existing arithmetic is correct by
    // construction, which is also the honest description of what Spark IS: a
    // companion standing next to the USER, not an object on the table.
    this.m_engine.scene.add(this.m_root);
    this.m_engine.scene.add(this.m_focusPoint);
    if (Ioc.isRegistered<PickingService>(PICKING_SERVICE)) {
      this.m_pickingService = Ioc.resolve<PickingService>(PICKING_SERVICE);
      this.m_pickingService.markUnpickable(this.m_root);
    }
    this.buildAttachments();
    void this.loadHeadAsync();
    void this.loadNimbusAsync();
    this.m_offFns.push(
      Ioc.resolve<FrameModel>(FRAME_MODEL).onTick.on(({ deltaSeconds }) =>
        this.tick(deltaSeconds),
      ),
      // Render on demand. A visible Spark is never still — the idle breathing
      // never stops — so it would keep the viewport drawing at the display's
      // full rate on its own; it reads the same at 30 fps (user direction,
      // 2026-10-01). Travelling across the scene is real motion and draws at
      // the full rate. Neither asks for a frame while Spark cannot be SEEN —
      // off the edge of the view, or behind an opaque cover (`isSeenOnScreen`).
      this.m_engine.addContinuousRenderSource({
        rate: "ambient",
        label: "Spark idle",
        isActive: () => this.isSeenOnScreen(),
      }),
      this.m_engine.addContinuousRenderSource({
        rate: "full",
        label: "Spark moving",
        isActive: () =>
          (this.m_sparkPositionVelocity.lengthSq() > K_RENDER_MOVING_SPEED_SQ
            || this.m_basePositionVelocity.lengthSq() > K_RENDER_MOVING_SPEED_SQ)
          && this.isSeenOnScreen(),
      }),
    );
    // Default gaze: the user. The orchestrator retargets to the model root
    // when a project loads (Unity `SetInitialObjectOfInterest`).
    this.lookAtUser();
  }

  dispose(): void {
    this.m_disposed = true;
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    if (this.m_nameTag) {
      disposeNameTag(this.m_nameTag);
      this.m_nameTag = null;
    }
    this.m_root.traverse((node) => {
      if ((node as Mesh).isMesh || (node as unknown as Line).isLine) {
        (node as Mesh).geometry?.dispose();
      }
    });
    this.m_glowMaterial.dispose();
    this.m_nimbusMaterial.dispose();
    this.m_nimbusTexture?.dispose();
    this.m_nimbusTexture = null;
    this.m_nimbusRings.length = 0;
    this.m_headModel = null;
    this.m_headPointerIntersections.length = 0;
    this.m_laserMaterial.dispose();
    this.m_pickingService = null;
    this.m_root.removeFromParent();
    this.m_focusPoint.removeFromParent();
    for (const laser of this.m_eyeLasers) {
      laser.geometry.dispose();
      laser.removeFromParent();
    }
    this.m_eyeLasers.length = 0;
  }

  // -- ISparkBehavior ------------------------------------------------------

  get isVisible(): boolean {
    return this.m_isVisible;
  }

  /** Exposes the continuous personal-space exchange to the event-driven viewport. */
  get isHeadPointerReactionActive(): boolean {
    return this.m_headIntrusionReaction.isActive;
  }

  setThinking(thinking: boolean): void {
    this.m_isThinking = thinking;
  }

  setInteracting(interacting: boolean): void {
    this.m_isInteracting = interacting;
  }

  setUserSpeaking(speaking: boolean): void {
    this.m_isUserSpeaking = speaking;
  }

  setAgentSpeaking(speaking: boolean): void {
    this.m_isAgentSpeaking = speaking;
  }

  setObjectOfInterest(node: Object3D | null, holdSeconds?: number): void {
    if (!node) return;
    this.m_objectOfInterest = node;
    // A pin CLEARS any hold rather than leaving one armed: otherwise the
    // previous glance's deadline would expire onto this pin and end it early.
    if (holdSeconds !== undefined && holdSeconds > 0) {
      this.m_gazeHoldNode = node;
      this.m_gazeHoldExpiresAtS = this.m_timeS + holdSeconds;
    } else {
      this.m_gazeHoldNode = null;
    }
  }

  lookAtUser(): void {
    this.m_objectOfInterest = this.m_camera;
    this.m_gazeHoldNode = null;
  }

  /**
   * Local pointing attention is deliberately outside ISparkBehavior: cursor
   * hover and the user's laser endpoint are viewport motor cues, not an
   * AI/orchestrator capability, and therefore have no role or tool-permission
   * gate. A null point starts a short miss grace instead of clearing
   * immediately: sparse meshes otherwise alternate hit/miss/hit and make the
   * head bounce between unrelated targets.
   */
  setPointerAttentionPoint(
    point: Readonly<{ x: number; y: number; z: number }> | null,
    subjectKey = "local-pointing",
    options: SparkPointerAttentionOptions = {},
  ): void {
    if (
      subjectKey === SPARK_HEAD_POINTER_SUBJECT &&
      !this.m_isHeadPointerHoverActive &&
      !this.m_headIntrusionReaction.isActive
    ) {
      // The viewport can still hold its last head candidate after a
      // lifecycle/gesture cancellation. Never let an unrelated later publish
      // resurrect that stale personal-space exchange.
      return;
    }
    // Finish a startled pointer-first reaction even if turning the head makes
    // its own mesh leave the pointer ray. A laser/button explicitly cancels
    // the reaction through `cancelHeadPointerReaction`; ordinary hover cues
    // wait for the short captured-pointer beat to complete.
    if (
      this.isHeadPointerReactionLocked() &&
      subjectKey !== SPARK_HEAD_POINTER_SUBJECT
    ) {
      return;
    }
    if (
      !point ||
      !subjectKey ||
      !Number.isFinite(point.x) ||
      !Number.isFinite(point.y) ||
      !Number.isFinite(point.z)
    ) {
      // A new subject that disappeared before the short acquisition delay was
      // only passed over; never pull the head toward it later.
      this.m_pendingPointerAttentionSubjectKey = null;
      if (this.m_hasPointerAttention && !Number.isFinite(this.m_pointerAttentionReleaseAtS)) {
        this.m_pointerAttentionReleaseAtS =
          this.m_timeS + POINTER_ATTENTION_GAP_GRACE_S;
      }
      return;
    }

    const checkIn = options.checkIn ?? "after-settle";
    const settleImmediately = options.settleImmediately === true;
    const updatedAtS = settleImmediately
      ? this.m_timeS - POINTER_ATTENTION_SETTLE_S
      : this.m_timeS;
    if (this.m_hasPointerAttention) this.m_pointerAttentionReleaseAtS = Infinity;

    if (subjectKey === this.m_pointerAttentionSubjectKey) {
      // Once Spark has committed to an object, follow the exact indicated point
      // continuously through its subject-specific spring, so model and the
      // faster personal-space path both remain eased between screen positions.
      this.m_pendingPointerAttentionSubjectKey = null;
      this.m_pointerAttentionGoal.set(point.x, point.y, point.z);
      this.m_pointerAttentionUpdatedAtS = updatedAtS;
      return;
    }

    if (options.acquireImmediately === true) {
      this.acquirePointerAttention(
        point,
        subjectKey,
        checkIn,
        settleImmediately,
      );
      return;
    }

    if (subjectKey !== this.m_pendingPointerAttentionSubjectKey) {
      this.m_pendingPointerAttentionSubjectKey = subjectKey;
      this.m_pendingPointerAttentionSinceS = this.m_timeS;
    }
    this.m_pendingPointerAttentionPoint.set(point.x, point.y, point.z);
    this.m_pendingPointerCheckIn = checkIn;
    this.m_pendingPointerSettleImmediately = settleImmediately;
  }

  /**
   * Project the live opaque skull and test a deliberately larger elliptical
   * personal-space halo. This is proximity, not collision: it makes attention
   * immediate and starts the local retreat/social reaction in the viewport.
   */
  isPointerInsidePersonalSpace(ndcX: number, ndcY: number): boolean {
    return this.pointerPersonalSpaceProximity(ndcX, ndcY) > 0;
  }

  /** 0 at the projected halo edge, 1 at the visible skull centre. */
  pointerPersonalSpaceProximity(ndcX: number, ndcY: number): number {
    if (
      !this.m_isVisible ||
      !this.m_root.visible ||
      !this.m_headModel ||
      !Number.isFinite(ndcX) ||
      !Number.isFinite(ndcY)
    ) {
      return 0;
    }
    // The personal halo belongs to the rendered head, not the standing root.
    // `measureVisibilityHeadEnvelope` reconstructs from the neck origin and
    // intentionally omits `m_neck.position`, so supply that live translated
    // origin here. This keeps the halo attached during avoidance motion.
    this.m_neck.getWorldPosition(this.m_visibilityHeadCenter);
    this.m_visibilityHeadCenter.y += HEAD_BASE_PIVOT_HEIGHT;
    this.measureVisibilityHeadEnvelope(this.m_visibilityHeadCenter);
    if (!this.m_visibilityHeadEnvelopeProjectable) return 0;
    const centerX =
      (this.m_visibilityHeadNdcLeft + this.m_visibilityHeadNdcRight) * 0.5;
    const centerY =
      (this.m_visibilityHeadNdcTop + this.m_visibilityHeadNdcBottom) * 0.5;
    const radiusX = Math.max(
      0.001,
      (this.m_visibilityHeadNdcRight - this.m_visibilityHeadNdcLeft) *
        0.5 * SPARK_PERSONAL_SPACE_SKULL_SCALE,
    );
    const radiusY = Math.max(
      0.001,
      (this.m_visibilityHeadNdcTop - this.m_visibilityHeadNdcBottom) *
        0.5 * SPARK_PERSONAL_SPACE_SKULL_SCALE,
    );
    const normalizedX = (ndcX - centerX) / radiusX;
    const normalizedY = (ndcY - centerY) / radiusY;
    return clamp01(1 - Math.hypot(normalizedX, normalizedY));
  }

  /**
   * Camera-forward plane for a cursor that has entered Spark's personal
   * space. It sits 10 cm in front of the visible face, rather than on the
   * user's monitor plane used by ordinary empty-screen cursor tracking.
   */
  personalSpaceAttentionForwardDepth(): number | null {
    if (
      !this.m_isVisible ||
      !this.m_root.visible ||
      !this.m_headModel ||
      this.m_disposed
    ) {
      return null;
    }
    this.m_camera.getWorldPosition(this.m_visibilityCameraPosition);
    this.m_camera.getWorldDirection(this.m_visibilityForward).normalize();
    this.m_headPivot.getWorldPosition(this.m_visibilitySamplePoint);
    const pivotDepth = this.m_visibilitySamplePoint
      .sub(this.m_visibilityCameraPosition)
      .dot(this.m_visibilityForward);
    const attentionDepth =
      pivotDepth -
      SPARK_VISIBLE_FACE_FORWARD_EXTENT_M -
      SPARK_PERSONAL_SPACE_FACE_CLEARANCE_M;
    return Number.isFinite(attentionDepth) &&
      attentionDepth > this.m_camera.near + 0.01
      ? attentionDepth
      : null;
  }

  /**
   * Raycast only Spark's loaded mannequin head. Scene picking deliberately
   * excludes Spark, so the viewport supplies the already-nearest scene hit for
   * the one ordering rule: content in front wins. The shader-faded bust is not
   * an interactive face; its native vertices below y=-0.3 are skipped.
   */
  hitTestVisibleHead(
    ray: Ray,
    nearestSceneHitDistance = Infinity,
  ): SparkVisibleHeadHit | null {
    const head = this.m_headModel;
    if (
      !head ||
      !this.m_isVisible ||
      !this.m_root.visible ||
      this.m_disposed
    ) {
      return null;
    }

    this.m_root.updateWorldMatrix(true, true);
    this.m_headPointerRaycaster.ray.copy(ray);
    this.m_headPointerRaycaster.near = 0;
    this.m_headPointerRaycaster.far = Number.isFinite(nearestSceneHitDistance)
      ? Math.max(0, nearestSceneHitDistance - SPARK_HEAD_HIT_SCENE_EPSILON_M)
      : Infinity;
    this.m_headPointerIntersections.length = 0;
    this.m_headPointerRaycaster.intersectObject(
      head,
      true,
      this.m_headPointerIntersections,
    );
    for (const hit of this.m_headPointerIntersections) {
      this.m_headPointerLocalHit.copy(hit.point);
      head.worldToLocal(this.m_headPointerLocalHit);
      if (this.m_headPointerLocalHit.y < SPARK_HEAD_OPAQUE_LOCAL_Y_MIN) continue;
      return {
        distance: hit.distance,
        worldPoint: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
      };
    }
    return null;
  }

  /**
   * Keep an anchored upper-body lateral dodge away from a cursor anywhere in
   * Spark's projected personal-space halo. Exact skull contact separately
   * starts the social reaction below.
   */
  setPersonalSpacePointerPoint(
    point: Readonly<{ x: number; y: number; z: number }> | null,
    proximity01 = 1,
  ): void {
    const isActive =
      point !== null &&
      Number.isFinite(point.x) &&
      Number.isFinite(point.y) &&
      Number.isFinite(point.z) &&
      this.m_isVisible &&
      this.m_root.visible &&
      !this.m_disposed;
    this.m_isPersonalSpacePointerActive = isActive;
    if (isActive) {
      this.m_headPointerProximity01 = clamp01(proximity01);
      this.setHeadPointerLeanTarget(point);
    } else if (
      !this.m_isHeadPointerHoverActive &&
      !this.m_headIntrusionReaction.isActive
    ) {
      this.resetHeadPointerDodgeMotor();
      this.m_headPointerLeanTarget.set(0, 0, 0);
    }
  }

  /** Begin the pointer-first exchange while personal-space contact remains. */
  triggerHeadPointerReaction(
    point: Readonly<{ x: number; y: number; z: number }>,
  ): void {
    if (!this.m_isVisible || !this.m_root.visible) return;
    this.m_isHeadPointerHoverActive = true;
    this.setPointerAttentionPoint(point, SPARK_HEAD_POINTER_SUBJECT, {
      acquireImmediately: true,
      checkIn: "none",
      settleImmediately: true,
    });
    this.m_headIntrusionReaction.trigger();
    // The viewport captures an uncalibrated cursor point for avoidance before
    // supplying the mannequin-calibrated gaze point here. Direct callers that
    // did not establish personal-space avoidance still get a safe fallback.
    if (!this.m_hasHeadPointerAvoidancePoint) {
      this.m_headPointerProximity01 = 1;
      this.setHeadPointerLeanTarget(point);
    }
  }

  /** Record current personal-space contact; viewport exit owns the end. */
  setHeadPointerHoverActive(active: boolean): void {
    this.m_isHeadPointerHoverActive = active;
  }

  /** Laser and pressed-pointer gestures cancel the lower-priority reaction. */
  cancelHeadPointerReaction(): void {
    this.m_isPersonalSpacePointerActive = false;
    this.m_isHeadPointerHoverActive = false;
    this.m_headIntrusionReaction.cancel();
    this.m_isHeadIntrusionLookingAtUser = false;
    this.resetHeadPointerDodgeMotor();
    this.m_headPointerLeanTarget.set(0, 0, 0);
    if (this.m_pointerAttentionSubjectKey === SPARK_HEAD_POINTER_SUBJECT) {
      this.clearPointerAttention();
    }
  }

  /** End pointer attention immediately for lifecycle changes and gestures. */
  clearPointerAttention(): void {
    this.m_hasPointerAttention = false;
    this.m_pointerAttentionSubjectKey = null;
    this.m_pendingPointerAttentionSubjectKey = null;
    this.m_pointerAttentionReleaseAtS = Infinity;
    this.m_pointerImmediateCheckInPending = false;
    this.m_pointerBeforeTargetCheckInPending = false;
    this.m_pointerAttentionVelocity.set(0, 0, 0);
  }

  showSpark(): void {
    if (this.m_isVisible) return;
    this.m_idleMotion.reset();
    this.m_userCheckIn.reset();
    this.resetHeadPointerLean();
    // Position before showing so Spark doesn't slide in from a stale spot or
    // flash for one frame behind a model before the first visibility probe.
    this.resetVisibilityPlacement();
    this.calculateStandingPosition(this.m_root.position);
    this.selectInitialVisibilitySlot();
    this.calculateStandingPosition(this.m_root.position);
    this.m_currentEffectiveHeight = this.m_baseSparkPosition.y;
    this.m_smoothedStandingHeight.y =
      this.m_currentEffectiveHeight + this.m_visibilityVerticalOffsetTarget.y;
    this.m_standingHeightTarget.y = this.m_smoothedStandingHeight.y;
    this.m_cachedBaseStandingPosition.copy(this.m_baseSparkPosition);
    this.m_smoothedBaseStandingPosition.copy(this.m_cachedBaseStandingPosition);
    this.m_basePositionVelocity.set(0, 0, 0);
    this.m_sparkPositionVelocity.set(0, 0, 0);
    this.m_standingHeightVelocity.set(0, 0, 0);
    this.m_hasCachedStandingPosition = true;
    this.captureStandingPlanReference();
    this.m_isVisible = true;
    this.syncRenderVisibility();
  }

  hideSpark(): void {
    this.m_isVisible = false;
    this.m_isScreenOcclusionSuppressed = false;
    this.syncRenderVisibility();
    this.m_userCheckIn.reset();
    this.clearPointerAttention();
    this.resetHeadPointerLean();
    this.resetVisibilityPlacement();
  }

  /**
   * Shows or hides only the name tag below Spark's head. Kept separate from
   * `showSpark` / `hideSpark`: the deployment setting removes the label, not
   * the companion itself, and can change while the async tag image is loading.
   * Hidden is the default on both sides of this call — see the field.
   */
  setNameTagVisible(visible: boolean): void {
    this.m_isNameTagVisible = visible;
    if (this.m_nameTag) this.m_nameTag.visible = visible;
    this.m_engine.requestRender();
  }

  // -- Assets ---------------------------------------------------------------

  private buildAttachments(): void {
    for (const offset of EYE_OFFSETS) {
      const geometry = new BufferGeometry().setFromPoints([new Vector3(), new Vector3()]);
      const laser = new Line(geometry, this.m_laserMaterial);
      laser.name = "spark.eyeLaser";
      laser.frustumCulled = false;
      laser.visible = false;
      laser.userData.eyeOffset = new Vector3(...offset);
      // World-space endpoints (head to focus point) — same frame as the rest
      // of Spark, see `mount`.
      this.m_engine.scene.add(laser);
      this.m_eyeLasers.push(laser);
    }

    // Name tag billboard ("Spark" + picture disc). Anchored to the root so
    // it hangs steady below the head instead of pitching with the gaze.
    const picture = new Image();
    picture.onload = () => {
      if (this.m_disposed) return;
      const tag = createNameTag("Spark", "S", NAME_TAG_COLOR, picture);
      tag.name = "spark.nameTag";
      tag.position.set(0, NAME_TAG_OFFSET_Y, 0);
      tag.visible = this.m_isNameTagVisible;
      this.m_root.add(tag);
      this.m_nameTag = tag;
      this.m_engine.requestRender();
    };
    picture.onerror = () => {
      if (this.m_disposed) return;
      // Picture missing — fall back to the initials-disc tag.
      const tag = createNameTag("Spark", "S", NAME_TAG_COLOR);
      tag.name = "spark.nameTag";
      tag.position.set(0, NAME_TAG_OFFSET_Y, 0);
      tag.visible = this.m_isNameTagVisible;
      this.m_root.add(tag);
      this.m_nameTag = tag;
      this.m_engine.requestRender();
    };
    picture.src = NAME_TAG_URL;
  }

  private async loadHeadAsync(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(HEAD_URL);
      if (this.m_disposed) return;
      const head = gltf.scene;
      head.position.set(...HEAD_LOCAL_POSITION);
      head.rotation.y = HEAD_LOCAL_YAW_RAD;
      head.scale.setScalar(HEAD_LOCAL_SCALE);
      head.traverse((node) => {
        if ((node as Mesh).isMesh) {
          const mesh = node as Mesh;
          mesh.material = this.m_glowMaterial;
          mesh.renderOrder = 990;
          configureAvatarHeadShadowCast(mesh);
        }
      });
      this.m_headPivot.add(head);
      this.m_headModel = head;
      // Async: until this lands Spark has no bounds, so its idle source is off.
      this.m_engine.requestRender();
    } catch (err) {
      console.warn("[SparkBehavior] failed to load head mesh", err);
    }
  }

  /**
   * Prefab `Head/Thinking` — the `nimbus` crown Spark wears while it works
   * (see `setThinking` for why that is broader than "thinks").
   *
   * Degrades to nothing on failure, like `loadHeadAsync` above: the Spark head
   * is companion chrome, and the Spark panel's own edge ring already tells the
   * user a turn is in flight, so a missing crown costs nothing functional.
   */
  private async loadNimbusAsync(): Promise<void> {
    try {
      const [gltf, map] = await Promise.all([
        new GLTFLoader().loadAsync(NIMBUS_URL),
        new TextureLoader().loadAsync(NIMBUS_TEXTURE_URL),
      ]);
      if (this.m_disposed) {
        map.dispose();
        return;
      }

      // The digit sheet is a white-on-transparent MASK multiplied by a
      // display-authored tint, so it stays in the working space instead of
      // being decoded as sRGB — the call `xrHtmlTexture` and `XRHandHint`
      // already make for display data.
      map.colorSpace = NoColorSpace;
      map.wrapS = RepeatWrapping;
      map.wrapT = RepeatWrapping;
      map.repeat.set(NIMBUS_MAP_REPEAT[0], NIMBUS_MAP_REPEAT[1]);
      this.m_nimbusTexture = map;
      this.m_nimbusMaterial.map = map;
      this.m_nimbusMaterial.needsUpdate = true;

      const crown = gltf.scene;
      crown.name = "spark.thinking";
      crown.position.set(...NIMBUS_LOCAL_POSITION);
      crown.rotation.copy(NIMBUS_ROTATION);
      crown.scale.set(...NIMBUS_SCALE);
      crown.visible = false;

      crown.traverse((node) => {
        const mesh = node as Mesh;
        if (!mesh.isMesh) return;
        mesh.material = this.m_nimbusMaterial;
        // Above the head (990) so the bands in front of the face blend over it
        // rather than being ordered against it.
        mesh.renderOrder = 991;
        // The crown sits inside the head's own silhouette by design, so a
        // frustum test on its small local bounds is not worth the per-frame
        // cost of getting it wrong at the edge of view.
        mesh.frustumCulled = false;

        const speed = NIMBUS_SPIN_DEG_PER_S[mesh.name];
        if (speed === undefined) return;
        this.m_nimbusRings.push({
          node: mesh,
          // The GLB carries each ring's authored start phase as a pure-Z
          // quaternion, so reading `rotation.z` recovers it exactly.
          baseAngleRad: mesh.rotation.z,
          speedRadPerS: speed * DEG2RAD,
        });
      });

      this.m_headPivot.add(crown);
      this.m_thinkingIndicator = crown;
      this.m_engine.requestRender();

      // A name that stops matching leaves the crown on screen and perfectly
      // still, which reads as "the animation is broken" rather than "the table
      // is stale" — say which it is.
      if (this.m_nimbusRings.length === 0) {
        console.warn(
          "[SparkBehavior] thinking crown loaded but no ring matched " +
            `NIMBUS_SPIN_DEG_PER_S (expected ${Object.keys(NIMBUS_SPIN_DEG_PER_S).join(", ")}) — it will not spin`,
        );
      }
    } catch (err) {
      console.warn("[SparkBehavior] failed to load thinking crown", err);
    }
  }

  // -- Per-frame behavior (port of `Update()`) ------------------------------

  private tick(dt: number): void {
    if (dt <= 0) return;
    this.m_timeS += dt;

    if (this.m_thinkingIndicator) {
      const thinking = this.m_isThinking && this.m_isVisible;
      this.m_thinkingIndicator.visible = thinking;
      // Unity toggles the whole `Thinking` GameObject, which stops its
      // `Rotater`s with it — so the clock only advances while the crown is up
      // and each band resumes from the angle it stopped at.
      if (thinking) {
        this.m_nimbusSpinS += dt;
        for (const ring of this.m_nimbusRings) {
          ring.node.rotation.z = ring.baseAngleRad + ring.speedRadPerS * this.m_nimbusSpinS;
        }
      }
    }

    this.updateEyeLasers();

    this.expireTemporaryGaze();

    this.updatePointerAttention(dt);
    this.checkForNewObjectOfInterest();
    this.updateHeadIntrusionReaction(dt);
    this.updateUserCheckIn(dt);
    this.checkForNewHeadTarget();
    this.checkHeadTargetMovement();

    if (this.m_isVisible && this.m_currentObjectOfInterest) {
      this.continuouslyUpdateSecondaryBodyAdjustments(dt);
    }

    if (this.m_isVisible) {
      this.updateSparkPosition(dt);
    }

    // Focus-point follow / transition progression.
    if (this.m_currentHeadTarget) {
      const targetPosition = worldPositionOf(this.m_currentHeadTarget);
      if (this.m_currentHeadTarget === this.m_pointerAttentionTarget) {
        // The pointer target itself is critically damped toward the latest
        // hit. Follow that continuous point directly rather than chasing a
        // stale entry Bézier endpoint.
        this.m_isTransitioning = false;
        this.m_focusPoint.position.copy(targetPosition);
      } else if (this.m_isHeadTargetMoving) {
        // Cancel transition so we can follow live movement.
        this.m_isTransitioning = false;
        this.m_focusPoint.position.copy(targetPosition);
      } else if (this.m_isTransitioning) {
        this.progressTransition(dt);
      } else {
        this.m_focusPoint.position.copy(targetPosition);
      }
    } else if (this.m_isTransitioning) {
      this.progressTransition(dt);
    }

    this.lookAtFocusPoint(dt);
  }

  /**
   * Ends a temporary look (see `setObjectOfInterest`) once its hold has run
   * out, returning to the resting gaze.
   *
   * The identity test is what makes the release safe by construction: a hold
   * may only ever end the look it was taken for, so it can never retarget a
   * gaze some other routing path has since set. The hold is dropped in the
   * same breath, which also keeps this safe if another semantic routing path
   * ever moves the gaze without going through `setObjectOfInterest`.
   *
   * ⚠ This test and the clear-on-pin in `setObjectOfInterest` MASK EACH OTHER:
   * seeded individually, neither is observable, because either one alone
   * covers every reachable path. They are belt and braces on purpose (the
   * clear needs no tick, the test needs no caller to remember), and the
   * negative control for them is the PAIR — removing one alone proves nothing.
   */
  private expireTemporaryGaze(): void {
    if (!this.m_gazeHoldNode) return;
    if (this.m_objectOfInterest !== this.m_gazeHoldNode) {
      this.m_gazeHoldNode = null;
      return;
    }
    if (this.m_timeS < this.m_gazeHoldExpiresAtS) return;
    this.lookAtUser();
  }

  private checkForNewObjectOfInterest(): void {
    if (this.m_objectOfInterest === this.m_currentObjectOfInterest) return;
    this.m_currentObjectOfInterest = this.m_objectOfInterest;
    this.m_pendingSemanticHeadTarget = this.m_currentObjectOfInterest;
  }

  private headAttentionAnchor(): Object3D | null {
    return this.m_hasPointerAttention
      ? this.m_pointerAttentionTarget
      : this.m_currentObjectOfInterest;
  }

  private updatePointerAttention(dt: number): void {
    if (
      this.m_pendingPointerAttentionSubjectKey !== null &&
      this.m_timeS - this.m_pendingPointerAttentionSinceS >=
        POINTER_ATTENTION_ACQUISITION_DELAY_S
    ) {
      this.acquirePointerAttention(
        this.m_pendingPointerAttentionPoint,
        this.m_pendingPointerAttentionSubjectKey,
        this.m_pendingPointerCheckIn,
        this.m_pendingPointerSettleImmediately,
      );
    }

    if (!this.m_hasPointerAttention) return;
    if (
      this.m_pointerAttentionSubjectKey === SPARK_HEAD_POINTER_SUBJECT &&
      !this.m_isHeadPointerHoverActive &&
      !this.m_headIntrusionReaction.isActive
    ) {
      this.clearPointerAttention();
      return;
    }
    if (this.m_timeS >= this.m_pointerAttentionReleaseAtS) {
      this.clearPointerAttention();
      return;
    }
    smoothDampVec3(
      this.m_pointerAttentionTarget.position,
      this.m_pointerAttentionGoal,
      this.m_pointerAttentionVelocity,
      this.hasPersonalSpacePointerAttention()
        ? PERSONAL_SPACE_ATTENTION_SMOOTHING_TIME_S
        : POINTER_ATTENTION_SMOOTHING_TIME_S,
      dt,
    );
    this.m_pointerAttentionTarget.updateMatrixWorld(true);
  }

  private acquirePointerAttention(
    point: Readonly<{ x: number; y: number; z: number }>,
    subjectKey: string,
    checkIn: NonNullable<SparkPointerAttentionOptions["checkIn"]>,
    settleImmediately: boolean,
  ): void {
    if (!this.m_hasPointerAttention) {
      // Enter from the pose that is already on screen. Immediate acquisition
      // removes only the subject dwell; this spring still supplies motor lag.
      this.m_pointerAttentionTarget.position.copy(this.m_focusPoint.position);
      this.m_pointerAttentionVelocity.set(0, 0, 0);
    }
    this.m_pointerAttentionSubjectKey = subjectKey;
    this.m_pointerAttentionGoal.set(point.x, point.y, point.z);
    this.m_pendingPointerAttentionSubjectKey = null;
    this.m_hasPointerAttention = true;
    this.m_pointerAttentionUpdatedAtS = settleImmediately
      ? this.m_timeS - POINTER_ATTENTION_SETTLE_S
      : this.m_timeS;
    this.m_pointerAttentionReleaseAtS = Infinity;
    this.m_pointerImmediateCheckInPending = checkIn === "after-settle";
    this.m_pointerBeforeTargetCheckInPending = checkIn === "before-target";
  }

  private isHeadPointerReactionLocked(): boolean {
    return (
      this.m_pointerAttentionSubjectKey === SPARK_HEAD_POINTER_SUBJECT &&
      (this.m_isHeadPointerHoverActive ||
        this.m_headIntrusionReaction.isActive)
    );
  }

  private desiredHeadTarget(): Object3D | null {
    if (this.m_isUserSpeaking || this.m_isAgentSpeaking) return this.m_camera;
    if (this.m_isHeadIntrusionLookingAtUser) return this.m_camera;
    return this.m_isCheckingInWithUser
      ? this.m_camera
      : this.headAttentionAnchor();
  }

  private updateHeadIntrusionReaction(dt: number): void {
    const wasActive = this.m_headIntrusionReaction.isActive;
    this.m_isHeadIntrusionLookingAtUser = this.m_headIntrusionReaction.update(
      dt,
      this.m_isVisible && !this.m_isUserSpeaking && !this.m_isAgentSpeaking,
    );
    if (wasActive && !this.m_headIntrusionReaction.isActive) {
      // Lifecycle and explicit gesture cancellations can end this controller
      // outside the pointer-event path, so release every captured cue here.
      this.m_isHeadPointerHoverActive = false;
      if (!this.m_isPersonalSpacePointerActive) {
        this.m_headPointerLeanTarget.set(0, 0, 0);
      }
      if (this.m_pointerAttentionSubjectKey === SPARK_HEAD_POINTER_SUBJECT) {
        this.clearPointerAttention();
      }
    }
  }

  private updateUserCheckIn(dt: number): void {
    const subject = this.headAttentionAnchor();
    if (subject !== this.m_checkInSubject) {
      this.m_checkInSubject = subject;
      this.m_userCheckIn.reset();
    }

    const hasNonUserSubject = subject !== null && subject !== this.m_camera;
    const enabled =
      this.m_isVisible &&
      !this.m_isThinking &&
      !this.m_isInteracting &&
      !this.m_isUserSpeaking &&
      !this.m_isAgentSpeaking &&
      !this.m_headIntrusionReaction.isActive &&
      hasNonUserSubject;
    const atUser =
      this.m_currentHeadTarget === this.m_camera && !this.m_isTransitioning;
    const atSubject =
      this.m_currentHeadTarget === subject && !this.m_isTransitioning;
    const pointerSettled = this.isPointerAttentionSettled();
    if (!pointerSettled && this.m_isCheckingInWithUser) {
      // A newly moving pointer is an active cue, so it interrupts a passive
      // social glance. Once the point rests, its check-in clock starts again.
      this.m_userCheckIn.reset();
    }
    const subjectStable = atSubject && !this.m_isHeadTargetMoving && pointerSettled;
    if (
      enabled &&
      subject === this.m_pointerAttentionTarget &&
      this.m_pointerBeforeTargetCheckInPending
    ) {
      // A pointer held over Spark's own face is a social cue first: meet the
      // user's eyes, then resolve the captured cursor direction. The captured
      // cue is marked settled, so its still-smoothing motor point cannot
      // cancel this deliberate reaction on the following frame.
      this.m_pointerBeforeTargetCheckInPending = false;
      this.m_userCheckIn.requestImmediate();
    }
    if (
      enabled &&
      subject === this.m_pointerAttentionTarget &&
      subjectStable &&
      this.m_pointerImmediateCheckInPending
    ) {
      // A newly acquired indicated point gets one immediate social check-in:
      // point → user → latest point. Consume before starting so movement
      // on this same subject can cancel the glance without re-triggering it.
      this.m_pointerImmediateCheckInPending = false;
      this.m_userCheckIn.requestImmediate();
    }

    this.m_isCheckingInWithUser = this.m_userCheckIn.update(
      dt,
      enabled,
      atUser,
      atSubject,
      subjectStable,
    );
  }

  private isPointerAttentionSettled(): boolean {
    const hasLocalPointingIntent =
      this.m_hasPointerAttention ||
      this.m_pendingPointerAttentionSubjectKey !== null;
    return (
      !hasLocalPointingIntent ||
      (this.m_pendingPointerAttentionSubjectKey === null &&
        this.m_timeS - this.m_pointerAttentionUpdatedAtS >=
          POINTER_ATTENTION_SETTLE_S)
    );
  }

  private hasPersonalSpacePointerAttention(): boolean {
    return (
      this.m_pointerAttentionSubjectKey === SPARK_PERSONAL_SPACE_SUBJECT ||
      this.m_pointerAttentionSubjectKey === SPARK_HEAD_POINTER_SUBJECT
    );
  }

  private checkForNewHeadTarget(): void {
    const desiredTarget = this.desiredHeadTarget();
    if (desiredTarget === this.m_currentHeadTarget) return;
    this.m_currentHeadTarget = desiredTarget;
    // A fixation is relative to one head target. Do not carry an offset into
    // a pointer point, check-in, or different co-located semantic part.
    this.m_idleMotion.reset();
    if (!this.m_currentHeadTarget) return;

    this.m_lastHeadTargetPosition.copy(worldPositionOf(this.m_currentHeadTarget));
    const alertPersonalSpacePointer =
      (this.m_pointerAttentionSubjectKey === SPARK_PERSONAL_SPACE_SUBJECT ||
        this.m_pointerAttentionSubjectKey === SPARK_HEAD_POINTER_SUBJECT) &&
      this.m_currentHeadTarget === this.m_pointerAttentionTarget;
    if (
      alertPersonalSpacePointer ||
      (this.m_headIntrusionReaction.isActive &&
        (this.m_currentHeadTarget === this.m_camera ||
          this.m_currentHeadTarget === this.m_pointerAttentionTarget))
    ) {
      // This is an alert exchange, not a leisurely idle glance. Put the focus
      // target in place immediately so rotation starts on this frame; the
      // neck/head rotation and local lean each use their own short visible
      // ease rather than the ordinary multi-second focus arc.
      this.m_isTransitioning = false;
      this.m_focusPoint.position.copy(this.m_lastHeadTargetPosition);
      return;
    }
    const delayBodyAdjustment =
      this.m_currentHeadTarget === this.m_pendingSemanticHeadTarget;
    this.moveFocusPoint(
      this.m_lastHeadTargetPosition,
      delayBodyAdjustment,
    );
    if (delayBodyAdjustment) this.m_pendingSemanticHeadTarget = null;
  }

  private checkHeadTargetMovement(): void {
    if (!this.m_currentHeadTarget) {
      this.m_isHeadTargetMoving = false;
      return;
    }
    const current = worldPositionOf(this.m_currentHeadTarget);
    this.m_isHeadTargetMoving =
      current.distanceTo(this.m_lastHeadTargetPosition) > OBJECT_MOVEMENT_THRESHOLD_M;
    this.m_lastHeadTargetPosition.copy(current);
  }

  private updateEyeLasers(): void {
    // Eye-laser rendering disabled — keep the code to restore later.
    void this.m_isInteracting;
    // const visible = this.m_isInteracting && this.m_isVisible;
    // let matricesFresh = false;
    // for (const laser of this.m_eyeLasers) {
    //   laser.visible = visible;
    //   if (!visible) continue;
    //   if (!matricesFresh) {
    //     this.m_headPivot.updateWorldMatrix(true, false);
    //     matricesFresh = true;
    //   }
    //   const eyeOffset = laser.userData.eyeOffset as Vector3;
    //   const start = this.m_headPivot.localToWorld(eyeOffset.clone());
    //   const positions = [start, this.m_focusPoint.position.clone()];
    //   laser.geometry.setFromPoints(positions);
    // }
  }

  // -- Focus point transitions (LeanTween arc port) --------------------------

  private moveFocusPoint(targetPosition: Vector3, delayBodyAdjustment = true): void {
    if (this.m_focusPoint.position.distanceTo(targetPosition) < 0.01) {
      // The requested target may be almost where this transition STARTED while
      // an older arc is still carrying the focus somewhere else. Speech is the
      // common case: Spark begins turning toward a part, then conversation
      // immediately asks for the camera again. "Already there" must cancel
      // that stale arc or it keeps pulling the head away during the sentence.
      this.m_focusPoint.position.copy(targetPosition);
      this.m_isTransitioning = false;
      return;
    }

    this.m_transitionDuration = this.calculateRotationDuration(targetPosition);
    this.m_transitionStart.copy(this.m_focusPoint.position);
    this.m_transitionEnd.copy(targetPosition);
    this.m_transitionMid.copy(
      this.calculateArcMidpoint(this.m_transitionStart, this.m_transitionEnd),
    );
    this.m_transitionT = 0;
    this.m_isTransitioning = true;
    if (delayBodyAdjustment) {
      this.m_lastTransitionStartTimeS = this.m_timeS;
      this.m_shouldDelaySecondaryBodyAdjustment = true;
    }
  }

  private progressTransition(dt: number): void {
    this.m_transitionT += dt / Math.max(0.0001, this.m_transitionDuration);
    const t = Math.min(1, this.m_transitionT);
    const eased = easeInOutCubic(t);
    this.m_focusPoint.position.copy(
      bezierPoint(this.m_transitionStart, this.m_transitionMid, this.m_transitionEnd, eased),
    );
    if (t >= 1) {
      this.m_focusPoint.position.copy(this.m_transitionEnd);
      this.m_isTransitioning = false;
    }
  }

  private calculateRotationDuration(targetPosition: Vector3): number {
    const neckPos = worldPositionOf(this.m_neck);
    const currentDir = this.m_focusPoint.position.clone().sub(neckPos).normalize();
    const targetDir = targetPosition.clone().sub(neckPos).normalize();
    const angleDeg = currentDir.angleTo(targetDir) / DEG2RAD;
    return clamp(
      angleDeg / HEAD_ROTATION_SPEED_DEG_S,
      MIN_ROTATION_DURATION_S,
      MAX_ROTATION_DURATION_S,
    );
  }

  /** Horizontal arc that curves away from Spark's head. */
  private calculateArcMidpoint(startPos: Vector3, endPos: Vector3): Vector3 {
    const midpoint = startPos.clone().add(endPos).multiplyScalar(0.5);
    const neckPos = worldPositionOf(this.m_neck);
    const distance = startPos.distanceTo(endPos);
    const arcOffset = distance * ARC_HEIGHT_MULTIPLIER;

    const directionBetween = endPos.clone().sub(startPos).normalize();
    let horizontalPerpendicular = new Vector3()
      .crossVectors(directionBetween, UP)
      .normalize();
    if (horizontalPerpendicular.lengthSq() < 0.01) {
      const neckToMid = midpoint.clone().sub(neckPos).normalize();
      horizontalPerpendicular = new Vector3().crossVectors(neckToMid, UP).normalize();
    }

    const testPoint = midpoint.clone().addScaledVector(horizontalPerpendicular, arcOffset);
    const otherPoint = midpoint.clone().addScaledVector(horizontalPerpendicular, -arcOffset);
    return neckPos.distanceTo(testPoint) > neckPos.distanceTo(otherPoint)
      ? testPoint
      : otherPoint;
  }

  // -- Look-at (port of `LookAtFocusPoint`) ----------------------------------

  private lookAtFocusPoint(dt: number): void {
    const neckPos = worldPositionOf(this.m_neck);
    const direction = this.m_focusPoint.position.clone().sub(neckPos);
    if (direction.lengthSq() < DIRECTION_EPSILON_SQ) return;
    direction.normalize();

    // Neck forward convention is -Z (matches camera / participant forward).
    const targetYawRad = Math.atan2(-direction.x, -direction.z);
    let targetPitchDeg = Math.asin(clamp(direction.y, -1, 1)) / DEG2RAD;
    targetPitchDeg = clamp(targetPitchDeg, PITCH_CLAMP_MIN_DEG, PITCH_CLAMP_MAX_DEG);

    const attentionKind = this.m_currentHeadTarget === this.m_camera ? "user" : "model";
    const idleEnabled =
      this.m_isVisible &&
      !this.m_isThinking &&
      !this.m_isInteracting &&
      !this.m_isUserSpeaking &&
      !this.m_isAgentSpeaking &&
      !this.m_isHeadTargetMoving &&
      !this.m_isTransitioning &&
      this.isPointerAttentionSettled() &&
      this.m_currentHeadTarget !== null;
    const idleOffsets = this.m_idleMotion.update(
      dt,
      idleEnabled,
      attentionKind,
      idleEnabled && attentionKind === "model" ? this.targetAngularRadiusDeg(neckPos) : 0,
    );

    if (!this.m_isTransitioning) {
      const targetNeckYawRad = targetYawRad + idleOffsets.yawDeg * DEG2RAD;
      const targetHeadPitchRad = clamp(
        targetPitchDeg + idleOffsets.pitchDeg,
        PITCH_CLAMP_MIN_DEG,
        PITCH_CLAMP_MAX_DEG,
      ) * DEG2RAD;
      const targetHeadTiltRad = idleOffsets.rollDeg * DEG2RAD;

      // Personal-space pointer tracking is twice as responsive as ordinary
      // model gaze. WTF user checks retain their separately tuned moderate
      // spring: alert and deliberate, never a one-frame mechanical snap.
      const isPersonalSpacePointerGaze =
        this.hasPersonalSpacePointerAttention() &&
        this.m_currentHeadTarget === this.m_pointerAttentionTarget;
      const rotationSharpness =
        this.m_headIntrusionReaction.isActive &&
        this.m_currentHeadTarget === this.m_camera
          ? HEAD_INTRUSION_ROTATION_SHARPNESS
          : isPersonalSpacePointerGaze
            ? PERSONAL_SPACE_ROTATION_SHARPNESS
            : IDLE_ROTATION_SHARPNESS;
      const k = 1 - Math.exp(-rotationSharpness * dt);
      this.m_neck.rotation.y = lerpAngle(this.m_neck.rotation.y, targetNeckYawRad, k);
      this.m_headPivot.rotation.x = lerp(
        this.m_headPivot.rotation.x,
        targetHeadPitchRad,
        k,
      );
      this.m_headPivot.rotation.z = lerp(
        this.m_headPivot.rotation.z,
        targetHeadTiltRad,
        k,
      );
    } else {
      // During transitions apply rotations directly (arc supplies motion).
      this.m_neck.rotation.y = targetYawRad;
      this.m_headPivot.rotation.x = targetPitchDeg * DEG2RAD;
      this.m_headPivot.rotation.z = 0;
    }
  }

  /** Apparent half-width of the attended model, in degrees from Spark. */
  private targetAngularRadiusDeg(neckPosition: Vector3): number {
    if (!this.m_currentHeadTarget || this.m_currentHeadTarget === this.m_camera) {
      return 0;
    }
    if (this.m_currentHeadTarget === this.m_pointerAttentionTarget) return 0;
    const diameter = this.cachedCombinedMeshSize(this.m_currentHeadTarget);
    if (diameter <= 0) return 0;
    const distance = Math.max(
      MIN_TARGET_DISTANCE_M,
      neckPosition.distanceTo(this.m_focusPoint.position),
    );
    return Math.atan2(diameter * TARGET_RADIUS_FRACTION, distance) / DEG2RAD;
  }

  /**
   * If the user is looking at scene geometry, express that centre-screen hit
   * as a small offset from Spark's user-facing gaze. This raycast runs only
   * when the idle controller chooses a new fixation, never per frame.
   */
  private readSharedAttentionCue(): IdleVisualCue | null {
    const picker = this.m_pickingService;
    if (
      !picker ||
      this.m_currentHeadTarget !== this.m_camera ||
      this.m_isCheckingInWithUser
    ) {
      return null;
    }

    const { clientWidth, clientHeight } = this.m_engine.canvas;
    if (clientWidth <= 0 || clientHeight <= 0) return null;
    const hit = picker.pickFromScreenNow(
      clientWidth * SCREEN_CENTER_FRACTION,
      clientHeight * SCREEN_CENTER_FRACTION,
      (node) => picker.resolveSnapshotId(node) !== null,
    );
    if (!hit) return null;

    const neckPosition = worldPositionOf(this.m_neck);
    const userDirection = this.m_focusPoint.position.clone().sub(neckPosition);
    const cueDirection = new Vector3(
      hit.worldPoint.x,
      hit.worldPoint.y,
      hit.worldPoint.z,
    ).sub(neckPosition);
    if (
      userDirection.lengthSq() < DIRECTION_EPSILON_SQ ||
      cueDirection.lengthSq() < DIRECTION_EPSILON_SQ
    ) {
      return null;
    }
    userDirection.normalize();
    cueDirection.normalize();

    const userYawRad = Math.atan2(-userDirection.x, -userDirection.z);
    const cueYawRad = Math.atan2(-cueDirection.x, -cueDirection.z);
    const userPitchRad = Math.asin(clamp(userDirection.y, -1, 1));
    const cuePitchRad = Math.asin(clamp(cueDirection.y, -1, 1));
    return {
      yawOffsetDeg: shortestAngleDelta(userYawRad, cueYawRad) / DEG2RAD,
      pitchOffsetDeg: (cuePitchRad - userPitchRad) / DEG2RAD,
    };
  }

  // -- Standing placement (ports of `CalculateStandingPostion` et al.) -------

  private addCurrentBodyOffsets(out: Vector3): Vector3 {
    return out.add(this.m_currentSecondaryBodyOffset);
  }

  private subtractCurrentBodyOffsets(out: Vector3): Vector3 {
    return out.sub(this.m_currentSecondaryBodyOffset);
  }

  private resetHeadPointerLean(): void {
    this.m_isPersonalSpacePointerActive = false;
    this.m_isHeadPointerHoverActive = false;
    this.m_headIntrusionReaction.cancel();
    this.m_isHeadIntrusionLookingAtUser = false;
    this.resetHeadPointerDodgeMotor();
    this.m_headPointerLeanOffset.set(0, 0, 0);
    this.m_headPointerLeanTarget.set(0, 0, 0);
    this.m_headPointerLeanVelocity.set(0, 0, 0);
    this.m_neck.position.set(0, 0, 0);
  }

  private updateHeadPointerLean(dt: number): void {
    if (
      !this.m_isPersonalSpacePointerActive &&
      !this.m_isHeadPointerHoverActive &&
      !this.m_headIntrusionReaction.isActive
    ) {
      this.resetHeadPointerDodgeMotor();
      this.m_headPointerLeanTarget.set(0, 0, 0);
    }
    if (this.m_hasHeadPointerAvoidancePoint) {
      this.updateHeadPointerLeanTarget(dt);
    }
    smoothDampVec3(
      this.m_headPointerLeanOffset,
      this.m_headPointerLeanTarget,
      this.m_headPointerLeanVelocity,
      SPARK_PERSONAL_SPACE_LEAN_SMOOTHING_TIME_S,
      dt,
    );
    this.m_neck.position.copy(this.m_headPointerLeanOffset);
  }

  private setHeadPointerLeanTarget(
    point: Readonly<{ x: number; y: number; z: number }>,
  ): void {
    this.m_headPointerAvoidancePoint.set(point.x, point.y, point.z);
    if (!this.m_hasHeadPointerAvoidancePoint) {
      this.readHeadPointerCameraBasis();
      this.m_root.getWorldQuaternion(this.m_headPointerLeanRootRotation);
      this.m_headPointerWorldOffset
        .copy(this.m_headPointerLeanOffset)
        .applyQuaternion(this.m_headPointerLeanRootRotation);
      this.m_headPointerDodgeLateralM = this.m_headPointerWorldOffset.dot(
        this.m_headPointerCameraRight,
      );
      this.m_headPivot.getWorldPosition(this.m_headPointerWorldOffset);
      const pointerFromHeadM = SCRATCH_HEAD_POINTER_POINT
        .copy(this.m_headPointerAvoidancePoint)
        .sub(this.m_headPointerWorldOffset)
        .dot(this.m_headPointerCameraRight);
      if (Math.abs(pointerFromHeadM) > SPARK_PERSONAL_SPACE_DODGE_DEAD_ZONE_M) {
        this.m_headPointerDodgeBias = pointerFromHeadM > 0 ? -1 : 1;
      } else {
        this.m_camera.worldToLocal(this.m_headPointerWorldOffset);
        this.m_headPointerDodgeBias =
          this.m_headPointerWorldOffset.x >= 0 ? 1 : -1;
      }
    }
    this.m_hasHeadPointerAvoidancePoint = true;
  }

  private readHeadPointerCameraBasis(): void {
    this.m_camera.updateWorldMatrix(true, false);
    this.m_headPointerCameraRight
      .setFromMatrixColumn(this.m_camera.matrixWorld, 0)
      .normalize();
    this.m_headPointerCameraUp
      .setFromMatrixColumn(this.m_camera.matrixWorld, 1)
      .normalize();
  }

  private updateHeadPointerLeanTarget(dt: number): void {
    this.readHeadPointerCameraBasis();
    this.m_root.getWorldQuaternion(this.m_headPointerLeanRootRotation);
    this.m_headPointerLeanRootInverse
      .copy(this.m_headPointerLeanRootRotation)
      .invert();

    this.m_headPivot.getWorldPosition(this.m_headPointerWorldOffset);
    SCRATCH_HEAD_POINTER_POINT
      .copy(this.m_headPointerAvoidancePoint)
      .sub(this.m_headPointerWorldOffset);
    const pointerFromFaceRightM = SCRATCH_HEAD_POINTER_POINT.dot(
      this.m_headPointerCameraRight,
    );
    const persistentDirection: -1 | 1 =
      Math.abs(this.m_headPointerDodgeVelocityM_S) > 0.02
        ? this.m_headPointerDodgeVelocityM_S > 0 ? 1 : -1
        : Math.abs(this.m_headPointerDodgeLateralM) > 0.01
          ? this.m_headPointerDodgeLateralM > 0 ? 1 : -1
          : this.m_headPointerDodgeBias;
    const awayDirection: -1 | 1 =
      Math.abs(pointerFromFaceRightM) <=
      SPARK_PERSONAL_SPACE_DODGE_DEAD_ZONE_M
        ? persistentDirection
        : pointerFromFaceRightM > 0 ? -1 : 1;
    // Smoothstep makes the first movement at the halo edge subtle, then grows
    // continuously to the full 20 cm only as the cursor reaches the face.
    const proximity = this.m_headPointerProximity01;
    const proximityWeight = proximity * proximity * (3 - 2 * proximity);
    const desiredLateralM =
      awayDirection * SPARK_PERSONAL_SPACE_LEAN_M * proximityWeight;
    const targetVelocityM_S = clamp(
      (desiredLateralM - this.m_headPointerDodgeLateralM) *
        SPARK_PERSONAL_SPACE_DODGE_POSITION_GAIN_S,
      -SPARK_PERSONAL_SPACE_DODGE_MAX_SPEED_M_S,
      SPARK_PERSONAL_SPACE_DODGE_MAX_SPEED_M_S,
    );
    this.m_headPointerDodgeVelocityM_S = lerp(
      this.m_headPointerDodgeVelocityM_S,
      targetVelocityM_S,
      1 - Math.exp(-SPARK_PERSONAL_SPACE_DODGE_STEERING_SHARPNESS * dt),
    );
    this.m_headPointerDodgeLateralM = clamp(
      this.m_headPointerDodgeLateralM +
        this.m_headPointerDodgeVelocityM_S * Math.max(0, dt),
      -SPARK_PERSONAL_SPACE_LEAN_M,
      SPARK_PERSONAL_SPACE_LEAN_M,
    );
    if (
      Math.abs(this.m_headPointerDodgeLateralM) >=
        SPARK_PERSONAL_SPACE_LEAN_M - 1e-6 &&
      Math.sign(this.m_headPointerDodgeVelocityM_S) ===
        Math.sign(this.m_headPointerDodgeLateralM)
    ) {
      this.m_headPointerDodgeVelocityM_S = 0;
    }
    if (Math.abs(this.m_headPointerDodgeVelocityM_S) > 0.05) {
      this.m_headPointerDodgeBias =
        this.m_headPointerDodgeVelocityM_S > 0 ? 1 : -1;
    }

    const pointerVerticalM = -SCRATCH_HEAD_POINTER_POINT.dot(
      this.m_headPointerCameraUp,
    );
    const maxVerticalDodgeM =
      SPARK_PERSONAL_SPACE_MAX_VERTICAL_DODGE_M * proximityWeight;
    this.m_headPointerLeanTarget
      .copy(this.m_headPointerCameraRight)
      .multiplyScalar(this.m_headPointerDodgeLateralM)
      .addScaledVector(
        this.m_headPointerCameraUp,
        clamp(
          pointerVerticalM,
          -maxVerticalDodgeM,
          maxVerticalDodgeM,
        ),
      )
      .applyQuaternion(this.m_headPointerLeanRootInverse);
  }

  private resetHeadPointerDodgeMotor(): void {
    this.m_hasHeadPointerAvoidancePoint = false;
    this.m_headPointerDodgeLateralM = 0;
    this.m_headPointerDodgeVelocityM_S = 0;
    this.m_headPointerProximity01 = 0;
  }

  /**
   * Desktop single-user path: `SPARK_DISTANCE_FROM_USER` in front of the
   * camera, `SPARK_LATERAL_OFFSET_FROM_USER` to its left, at eye height.
   */
  private calculateStandingPosition(out: Vector3): void {
    this.m_cachedDynamicBaseHeight = this.computeDynamicBaseHeight();
    const targetHeight = this.m_cachedDynamicBaseHeight;

    this.readVisibilityCameraBasis();
    this.calculateVisibilitySlotBaseFromCurrentBasis(
      this.m_visibilityActiveSlot,
      targetHeight,
      out,
    );
    out.y = Math.max(SPARK_MIN_STANDING_Y, out.y);

    this.m_visibilityVerticalOffsetTarget.set(0, out.y - targetHeight, 0);
    this.m_baseSparkPosition.copy(out);
    this.m_baseSparkPosition.y = targetHeight;
    this.addCurrentBodyOffsets(out);
    out.y = targetHeight + this.m_visibilityVerticalOffsetTarget.y;
  }

  /** Reset search state without carrying a previous appearance's plan across. */
  private resetVisibilityPlacement(): void {
    this.m_visibilitySlotIndex = HOME_VISIBILITY_SLOT_INDEX;
    copyVisibilitySlot(
      this.m_visibilityActiveSlot,
      SPARK_VISIBILITY_SLOTS[HOME_VISIBILITY_SLOT_INDEX],
    );
    this.m_nextVisibilityProbeTimeS = this.m_timeS;
    this.m_visibilityBlockedSamples = 0;
    this.m_visibilityDestinationBlockedSamples = 0;
    this.m_visibilityEdgeProbeIndex = 0;
    this.m_visibilityRelocationLockedUntilS = -Infinity;
    this.m_visibilitySearchFoundImprovement = false;
    this.m_isScreenOcclusionSuppressed = false;
    this.m_visibilityVerticalOffsetTarget.set(0, 0, 0);
    this.m_smoothedStandingHeight.set(0, this.m_currentEffectiveHeight, 0);
    this.m_standingHeightTarget.copy(this.m_smoothedStandingHeight);
    this.m_standingHeightVelocity.set(0, 0, 0);
    this.m_hasStandingPlanReference = false;
    this.m_hasFailedStandingViewpoint = false;
    this.m_hasStandingCameraMotionSample = false;
    this.m_standingCameraLastMotionS = -Infinity;
  }

  /**
   * Do the expensive search once before revealing Spark. This is intentionally
   * synchronous: showing at home and correcting on the next frame produces a
   * conspicuous flash through foreground content.
   */
  private selectInitialVisibilitySlot(): void {
    this.refreshVisibilityScreenOccluders();
    this.readVisibilityCameraBasis();
    this.setVisibilityHeadCenterFromBase(
      this.m_baseSparkPosition,
      this.m_visibilityHeadCenter,
    );
    const homeEnvelopeInFrame = this.measureVisibilityHeadEnvelope(
      this.m_visibilityHeadCenter,
      true,
    );
    const homeScreenBlocked =
      this.m_visibilityHeadEnvelopeProjectable &&
      this.visibilityHeadEnvelopeOverlapsScreenOccluder();
    const homeCenterBlocked =
      !homeEnvelopeInFrame ||
      homeScreenBlocked ||
      this.isVisibilitySkullSampleOccluded(this.m_visibilityHeadCenter, 0);
    const homeScore = this.visibilityScoreForBase(
      this.m_baseSparkPosition,
      !homeCenterBlocked,
    );
    if (
      !homeCenterBlocked &&
      homeScore >= VISIBILITY_ACCEPTABLE_SAMPLE_COUNT
    ) {
      return;
    }

    const bestSlot = this.findBestVisibilitySlot(
      this.m_baseSparkPosition.y,
      homeScore,
      homeCenterBlocked,
    );
    if (this.m_visibilitySearchFoundImprovement) {
      this.adoptVisibilitySearchSlot(bestSlot);
      this.m_visibilityRelocationLockedUntilS =
        this.m_timeS + VISIBILITY_RELOCATION_LOCK_S;
      return;
    }
    // A panel can cover the entire usable canvas. There is no honest visible
    // placement in that state, so withhold the render until a slot opens rather
    // than drawing Spark underneath UI that was declared non-occluding.
    this.m_isScreenOcclusionSuppressed = homeScreenBlocked;
  }

  /**
   * Validate the COMMITTED world spot. At rest this is two rays at 4 Hz — it
   * does not recalculate or remap the standing target. A search happens only
   * after confirmed depth occlusion, deterministic DOM overlap, or a settled
   * meaningful viewpoint change.
   */
  private updateVisibilityPlacement(): void {
    const depthProbeDue = this.m_timeS >= this.m_nextVisibilityProbeTimeS;
    if (depthProbeDue) {
      this.m_nextVisibilityProbeTimeS =
        this.m_timeS + VISIBILITY_PROBE_INTERVAL_S;
    }
    this.refreshVisibilityScreenOccluders();

    this.readVisibilityCameraBasis();
    const cameraIsSettled = this.updateStandingCameraMotionState();
    this.m_visibilityHeadCenter.copy(this.m_root.position);
    this.m_visibilityHeadCenter.y += HEAD_BASE_PIVOT_HEIGHT;
    const currentEnvelopeInFrame = this.measureVisibilityHeadEnvelope(
      this.m_visibilityHeadCenter,
    );
    const currentScreenBlocked =
      this.m_visibilityHeadEnvelopeProjectable &&
      this.visibilityHeadEnvelopeOverlapsScreenOccluder();
    const currentSceneCenterBlocked =
      depthProbeDue &&
      this.isVisibilitySkullSampleOccluded(this.m_visibilityHeadCenter, 0);
    const currentCenterBlocked =
      !currentEnvelopeInFrame ||
      currentScreenBlocked ||
      currentSceneCenterBlocked;
    let committedDestinationOutsideFrame = false;
    let committedDestinationScreenBlocked = false;
    let committedDestinationSceneBlocked = false;
    let standingRelocationInFlight = false;
    if (this.m_hasCachedStandingPosition) {
      this.m_visibilityDisplacement
        .copy(this.m_smoothedBaseStandingPosition);
      this.addCurrentBodyOffsets(this.m_visibilityDisplacement);
      this.m_visibilityDisplacement.y = this.m_smoothedStandingHeight.y;
      standingRelocationInFlight =
        this.m_smoothedBaseStandingPosition.distanceTo(
          this.m_cachedBaseStandingPosition,
        ) > VISIBILITY_DESTINATION_SETTLE_DISTANCE_M ||
        Math.abs(
          this.m_smoothedStandingHeight.y - this.m_standingHeightTarget.y,
        ) > VISIBILITY_DESTINATION_SETTLE_DISTANCE_M ||
        this.m_visibilityDisplacement.distanceTo(this.m_root.position) >
          VISIBILITY_DESTINATION_SETTLE_DISTANCE_M;
      // Distinguish a harmless spring path from a stale standing height. If
      // the rendered head is out of view but its destination is valid, let the
      // relocation finish. If BOTH are outside (notably after the user lowers
      // the camera from an elevated view), the old world anchor is invalid and
      // must not be protected by its relocation cooldown.
      this.m_visibilityCurrentBase.copy(this.m_cachedBaseStandingPosition);
      this.m_visibilityCurrentBase.y += this.m_visibilityVerticalOffsetTarget.y;
      this.setVisibilityHeadCenterFromBase(
        this.m_visibilityCurrentBase,
        this.m_visibilityHeadCenter,
      );
      const committedEnvelopeInFrame = this.measureVisibilityHeadEnvelope(
        this.m_visibilityHeadCenter,
        true,
      );
      committedDestinationOutsideFrame = !committedEnvelopeInFrame;
      committedDestinationScreenBlocked =
        this.m_visibilityHeadEnvelopeProjectable &&
        this.visibilityHeadEnvelopeOverlapsScreenOccluder();
      if (
        committedEnvelopeInFrame &&
        !committedDestinationScreenBlocked &&
        depthProbeDue
      ) {
        if (!standingRelocationInFlight) {
          committedDestinationSceneBlocked = currentSceneCenterBlocked;
        } else {
          committedDestinationSceneBlocked =
            this.visibilityScoreAtHeadCenterForDestination(
              this.m_visibilityHeadCenter,
            ) < VISIBILITY_ACCEPTABLE_SAMPLE_COUNT;
        }
      }
      this.m_visibilityHeadCenter.copy(this.m_root.position);
      this.m_visibilityHeadCenter.y += HEAD_BASE_PIVOT_HEIGHT;
      this.measureVisibilityHeadEnvelope(this.m_visibilityHeadCenter);
    }

    // DOM boxes are cheap and authoritative, so sample them every rendered
    // frame. CAD depth rays and ordinary viewpoint checks keep their 4 Hz
    // cadence. A fully covered hidden Spark retries candidate search at that
    // cadence too rather than raycasting every frame while nothing changed.
    if (
      !depthProbeDue &&
      !currentScreenBlocked &&
      !committedDestinationScreenBlocked &&
      !this.m_isScreenOcclusionSuppressed
    ) {
      return;
    }
    if (
      !depthProbeDue &&
      this.m_isScreenOcclusionSuppressed &&
      currentScreenBlocked &&
      committedDestinationScreenBlocked
    ) {
      return;
    }
    if (!depthProbeDue && this.m_isScreenOcclusionSuppressed) {
      // UI may have just uncovered a scene obstruction. Keep the logical Spark
      // hidden until one real depth pass proves the revealed position rather
      // than flashing it for a frame between compositor and CAD probes.
      this.m_nextVisibilityProbeTimeS = this.m_timeS;
      return;
    }

    // A Spark already travelling out from under glass keeps that one clear
    // destination until its rendered envelope emerges. This remains
    // true for the last few centimetres after the numerical spring threshold.
    if (
      currentScreenBlocked &&
      !committedDestinationOutsideFrame &&
      !committedDestinationScreenBlocked &&
      !committedDestinationSceneBlocked
    ) {
      this.m_visibilityBlockedSamples = 0;
      this.m_visibilityDestinationBlockedSamples = 0;
      return;
    }

    // Detect DOM overlap every frame, but perform the candidate raycast on the
    // normal depth-probe cadence. Scheduling the next frame gives panel motion
    // a sub-frame response without turning a drag into hundreds of CAD rays.
    if (
      !depthProbeDue &&
      (currentScreenBlocked || committedDestinationScreenBlocked)
    ) {
      this.m_nextVisibilityProbeTimeS = this.m_timeS;
      return;
    }

    let currentScore: number | null = null;
    let currentPlacementBlocked = currentCenterBlocked;
    if (depthProbeDue && this.m_isScreenOcclusionSuppressed) {
      // Clearing a DOM box only says the compositor stopped covering this
      // point. Before revealing a logically hidden Spark, force the full live
      // plus pose-reserve skull score: centre mandatory, six perimeter rays,
      // and at most one small edge overlap. The ordinary rotating edge probe
      // is intentionally too cheap to serve as a reveal certificate.
      currentScore = this.visibilityScoreAtHeadCenterForDestination(
        this.m_visibilityHeadCenter,
      );
      currentPlacementBlocked =
        currentScore < VISIBILITY_ACCEPTABLE_SAMPLE_COUNT;
    } else if (
      depthProbeDue &&
      !currentCenterBlocked &&
      (this.m_visibilityBlockedSamples > 0 ||
        this.isNextVisibilityEnvelopeSampleOccluded(
          this.m_visibilityHeadCenter,
        ))
    ) {
      currentScore = this.visibilityScoreAtHeadCenter(
        this.m_visibilityHeadCenter,
        true,
      );
      currentPlacementBlocked =
        currentScore < VISIBILITY_ACCEPTABLE_SAMPLE_COUNT;
    }

    const committedDestinationInvalid =
      standingRelocationInFlight &&
      (committedDestinationOutsideFrame ||
        committedDestinationScreenBlocked ||
        committedDestinationSceneBlocked);

    // The panel may still cover the spring's CURRENT point after a clear
    // destination has been committed. Keep travelling toward that proved spot
    // rather than re-running the search every probe and turning one smooth
    // move into a succession of jumps. Moving/resizing the panel over the
    // destination invalidates it immediately and starts one new plan.
    if (
      standingRelocationInFlight &&
      currentPlacementBlocked &&
      !committedDestinationInvalid
    ) {
      this.m_visibilityBlockedSamples = 0;
      this.m_visibilityDestinationBlockedSamples = 0;
      return;
    }

    // A panel dragged over Spark is exact compositor geometry, not a noisy
    // mesh edge: it overrides the relocation lock and the two-sample depth
    // debounce. Everything else keeps the lock so a freshly chosen spot can
    // settle before another viewpoint decision is admitted.
    if (
      this.m_timeS < this.m_visibilityRelocationLockedUntilS &&
      !currentScreenBlocked &&
      !this.m_isScreenOcclusionSuppressed &&
      !committedDestinationInvalid &&
      !committedDestinationOutsideFrame
    ) {
      return;
    }

    const destinationScreenReplan =
      standingRelocationInFlight && committedDestinationScreenBlocked;
    const destinationSceneBlocked =
      standingRelocationInFlight && committedDestinationSceneBlocked;
    const viewpointReplan =
      cameraIsSettled &&
      this.shouldReplanStandingForViewpoint(
        committedDestinationOutsideFrame,
      );
    if (
      !currentPlacementBlocked &&
      !viewpointReplan &&
      !destinationScreenReplan &&
      !destinationSceneBlocked
    ) {
      this.m_visibilityBlockedSamples = 0;
      this.m_visibilityDestinationBlockedSamples = 0;
      if (this.m_isScreenOcclusionSuppressed) {
        this.m_isScreenOcclusionSuppressed = false;
        this.syncRenderVisibility();
      }
      return;
    }

    const canConfirmScene =
      !destinationScreenReplan && !this.m_isScreenOcclusionSuppressed;
    const needsCurrentSceneConfirmation =
      canConfirmScene && currentPlacementBlocked && !currentScreenBlocked;
    const needsDestinationSceneConfirmation =
      canConfirmScene && destinationSceneBlocked;
    if (needsCurrentSceneConfirmation) {
      this.m_visibilityBlockedSamples += 1;
    } else {
      this.m_visibilityBlockedSamples = 0;
    }
    if (needsDestinationSceneConfirmation) {
      this.m_visibilityDestinationBlockedSamples += 1;
    } else {
      this.m_visibilityDestinationBlockedSamples = 0;
    }
    if (needsCurrentSceneConfirmation || needsDestinationSceneConfirmation) {
      const currentSceneConfirmed =
        this.m_visibilityBlockedSamples >= VISIBILITY_BLOCKED_SAMPLE_COUNT;
      const destinationSceneConfirmed =
        this.m_visibilityDestinationBlockedSamples >=
        VISIBILITY_BLOCKED_SAMPLE_COUNT;
      const hasDeterministicReplanTrigger =
        currentScreenBlocked || destinationScreenReplan || viewpointReplan;
      if (
        !hasDeterministicReplanTrigger &&
        !currentSceneConfirmed &&
        !destinationSceneConfirmed
      ) {
        return;
      }
      // Occlusion while the user is orbiting is provisional: a model edge can
      // cross Spark several times during one gesture. Hold the world spot and
      // make at most one decision after the viewpoint has settled.
      if (!cameraIsSettled) return;
    }

    const destinationReplan =
      destinationScreenReplan || destinationSceneBlocked;
    currentScore ??= this.visibilityScoreAtHeadCenter(
      this.m_visibilityHeadCenter,
      !currentCenterBlocked,
    );
    // Search where the NEXT plan would stand, not at the old smoothed body
    // height. Reusing `m_currentEffectiveHeight` made every candidate remain
    // above the frame after descending from an elevated camera position.
    const prospectiveStandingHeight = this.computeDynamicBaseHeight();
    const forceReplacement = viewpointReplan || destinationReplan;
    const bestSlot = this.findBestVisibilitySlot(
      prospectiveStandingHeight,
      forceReplacement ? -1 : currentScore,
      forceReplacement || currentCenterBlocked,
      forceReplacement,
      !viewpointReplan && (currentPlacementBlocked || destinationReplan),
    );
    this.m_visibilityBlockedSamples = 0;
    this.m_visibilityDestinationBlockedSamples = 0;
    if (this.m_visibilitySearchFoundImprovement) {
      this.adoptVisibilitySearchSlot(bestSlot);
      if (this.m_isScreenOcclusionSuppressed) {
        // When no fit existed Spark was not painted, so arrive at the first
        // proved-clear destination before revealing it. An already-visible
        // Spark uses the normal damped relocation below, including for panels.
        this.m_isScreenOcclusionSuppressed = true;
        this.syncRenderVisibility();
        this.snapVisibilityStandingTarget();
      } else {
        this.refreshVisibilityStandingTarget();
      }
      this.m_isScreenOcclusionSuppressed = false;
      this.syncRenderVisibility();
      return;
    }

    if (
      !currentPlacementBlocked &&
      currentScore >= VISIBILITY_ACCEPTABLE_SAMPLE_COUNT &&
      (destinationReplan ||
        (viewpointReplan && committedDestinationOutsideFrame))
    ) {
      // A spring destination can become hidden while the rendered pose is
      // still clear. If no replacement exists, cancel at the visible point;
      // otherwise the old velocity would carry Spark into the obstruction and
      // make every later probe fight the same invalid endpoint.
      this.holdCurrentRenderedStandingPosition();
      this.markFailedStandingViewpoint();
      this.m_isScreenOcclusionSuppressed = false;
      this.syncRenderVisibility();
      return;
    }

    if (
      currentScreenBlocked ||
      committedDestinationScreenBlocked ||
      this.m_isScreenOcclusionSuppressed
    ) {
      // No candidate can clear the UI right now. A hidden logical Spark keeps
      // its behavior state and retries promptly; revealing at the old point
      // would violate the panel's hard exclusion contract.
      this.m_isScreenOcclusionSuppressed = true;
      this.syncRenderVisibility();
      this.m_nextVisibilityProbeTimeS =
        this.m_timeS + VISIBILITY_PROBE_INTERVAL_S;
    } else if (viewpointReplan) {
      // The old spot is still visible and no better socially-relative slot
      // exists. Accept this viewpoint as the new reference so we do not retry
      // the same failed plan every probe.
      this.captureStandingPlanReference();
      this.markFailedStandingViewpoint();
    } else {
      // A scene with no visible candidate should not make Spark wander.
      this.m_nextVisibilityProbeTimeS =
        this.m_timeS + VISIBILITY_FAILED_SEARCH_RETRY_S;
    }
  }

  private adoptVisibilitySearchSlot(slotIndex: number): void {
    const slot = this.m_visibilitySearchSlots[slotIndex];
    if (!slot) return;
    this.m_visibilitySlotIndex = slotIndex;
    copyVisibilitySlot(this.m_visibilityActiveSlot, slot);
  }

  private refreshVisibilityStandingTarget(): void {
    this.m_visibilityBlockedSamples = 0;
    this.m_visibilityDestinationBlockedSamples = 0;
    this.m_hasFailedStandingViewpoint = false;
    this.m_visibilityRelocationLockedUntilS =
      this.m_timeS + VISIBILITY_RELOCATION_LOCK_S;

    // Commit ONE world-space destination. Keep the smoothed base where it is
    // so the existing two-stage spring supplies the visible step, then hold the
    // accepted point until a real replan trigger arrives.
    this.calculateStandingPosition(TMP_VEC);
    this.m_cachedBaseStandingPosition.copy(this.m_baseSparkPosition);
    this.m_currentEffectiveHeight = this.m_cachedDynamicBaseHeight;
    this.m_standingHeightTarget.y =
      this.m_currentEffectiveHeight + this.m_visibilityVerticalOffsetTarget.y;
    this.m_basePositionVelocity.set(0, 0, 0);
    this.m_sparkPositionVelocity.set(0, 0, 0);
    this.m_hasCachedStandingPosition = true;
    this.captureStandingPlanReference();
  }

  /** A hidden Spark can jump to its proved-clear target before being revealed. */
  private snapVisibilityStandingTarget(): void {
    this.m_hasFailedStandingViewpoint = false;
    this.calculateStandingPosition(this.m_root.position);
    this.m_cachedBaseStandingPosition.copy(this.m_baseSparkPosition);
    this.m_smoothedBaseStandingPosition.copy(this.m_cachedBaseStandingPosition);
    this.m_currentEffectiveHeight = this.m_cachedDynamicBaseHeight;
    this.m_smoothedStandingHeight.y =
      this.m_currentEffectiveHeight + this.m_visibilityVerticalOffsetTarget.y;
    this.m_standingHeightTarget.y = this.m_smoothedStandingHeight.y;
    this.m_basePositionVelocity.set(0, 0, 0);
    this.m_sparkPositionVelocity.set(0, 0, 0);
    this.m_standingHeightVelocity.set(0, 0, 0);
    this.m_hasCachedStandingPosition = true;
    this.captureStandingPlanReference();
  }

  /** Cancel an invalid in-flight destination without moving the visible pose. */
  private holdCurrentRenderedStandingPosition(): void {
    this.m_cachedBaseStandingPosition
      .copy(this.m_root.position);
    this.subtractCurrentBodyOffsets(this.m_cachedBaseStandingPosition);
    const heldStandingY = Math.max(
      SPARK_MIN_STANDING_Y,
      this.m_root.position.y,
    );
    this.m_cachedBaseStandingPosition.y = heldStandingY;
    this.m_smoothedBaseStandingPosition.copy(this.m_cachedBaseStandingPosition);
    this.m_baseSparkPosition.copy(this.m_cachedBaseStandingPosition);
    this.m_cachedDynamicBaseHeight = heldStandingY;
    this.m_currentEffectiveHeight = heldStandingY;
    this.m_visibilityVerticalOffsetTarget.set(0, 0, 0);
    this.m_smoothedStandingHeight.y = heldStandingY;
    this.m_standingHeightTarget.y = heldStandingY;
    this.m_basePositionVelocity.set(0, 0, 0);
    this.m_sparkPositionVelocity.set(0, 0, 0);
    this.m_standingHeightVelocity.set(0, 0, 0);

    this.m_visibilityHeadCenter.copy(this.m_root.position);
    this.m_visibilityHeadCenter.y += HEAD_BASE_PIVOT_HEIGHT;
    this.m_visibilityProjection.copy(this.m_visibilityHeadCenter).project(this.m_camera);
    this.m_visibilityDisplacement
      .copy(this.m_visibilityHeadCenter)
      .sub(this.m_visibilityCameraPosition);
    const forwardDepth = this.m_visibilityDisplacement.dot(this.m_visibilityForward);
    if (
      Number.isFinite(this.m_visibilityProjection.x) &&
      Number.isFinite(this.m_visibilityProjection.y) &&
      forwardDepth > 0.05
    ) {
      this.m_visibilityActiveSlot.ndcX = this.m_visibilityProjection.x;
      this.m_visibilityActiveSlot.ndcY = this.m_visibilityProjection.y;
      this.m_visibilityActiveSlot.forwardDepthM = forwardDepth;
      this.m_visibilitySlotIndex = -1;
    }
    this.m_visibilityBlockedSamples = 0;
    this.m_visibilityDestinationBlockedSamples = 0;
    this.m_visibilityRelocationLockedUntilS =
      this.m_timeS + VISIBILITY_RELOCATION_LOCK_S;
    this.m_hasCachedStandingPosition = true;
    this.captureStandingPlanReference();
  }

  /**
   * Sort all centre-clear slots by travel cost, then validate each against the
   * visible-skull sample set. Local screen-space rings let a small up/down or
   * diagonal adjustment beat a coarse move around the model; fixed social
   * anchors and exact panel-edge candidates remain the farther fallbacks.
   */
  private findBestVisibilitySlot(
    targetHeight: number,
    currentScore: number,
    currentCenterBlocked: boolean,
    forceReplacement = false,
    allowLocalRecovery = true,
  ): number {
    this.m_visibilitySearchFoundImprovement = false;
    if (
      !forceReplacement &&
      !currentCenterBlocked &&
      currentScore >= VISIBILITY_ACCEPTABLE_SAMPLE_COUNT
    ) {
      return this.m_visibilitySlotIndex;
    }
    this.readVisibilityCameraBasis();
    this.calculateVisibilitySlotBaseFromCurrentBasis(
      SPARK_VISIBILITY_SLOTS[HOME_VISIBILITY_SLOT_INDEX],
      targetHeight,
      this.m_visibilityHomeBase,
    );
    // Cost against the actual rendered WORLD pose, not this slot remapped
    // through the new camera. During an interrupted spring this is the point a
    // viewer sees, so the replacement is the smallest physical correction.
    if (this.m_hasCachedStandingPosition) {
      this.m_visibilityCurrentBase
        .copy(this.m_root.position);
      this.subtractCurrentBodyOffsets(this.m_visibilityCurrentBase);
      this.m_visibilityCurrentBase.y = this.m_root.position.y;
    } else {
      this.m_visibilityCurrentBase.copy(this.m_baseSparkPosition);
    }
    this.buildVisibilitySearchSlots(allowLocalRecovery);
    for (const finalist of this.m_visibilityFinalists) {
      finalist.slotIndex = -1;
      finalist.cost = Infinity;
    }

    for (let slotIndex = 0; slotIndex < this.m_visibilitySearchSlotCount; slotIndex += 1) {
      const slot = this.m_visibilitySearchSlots[slotIndex];
      this.calculateVisibilitySlotBaseFromCurrentBasis(
        slot,
        targetHeight,
        this.m_visibilityCandidateBase,
      );
      if (this.m_visibilityCandidateBase.y < SPARK_MIN_STANDING_Y) {
        // Empty space under the floor is not an unoccluded fallback. Reject it
        // before sorting/raycasting so a low screen slot can never win merely
        // because no scene mesh exists beneath the ground plane.
        continue;
      }
      this.insertVisibilityFinalist(
        slotIndex,
        this.visibilityCandidateCost(
          slot,
          this.m_visibilityCandidateBase,
          allowLocalRecovery,
        ),
        this.m_visibilityCandidateBase,
      );
    }

    // Sort before doing any scene raycasts: most obstructions resolve on the
    // first local ring, so there is no reason to intersect the entire CAD tree
    // for every farther fallback. Six visible skull points is the requested
    // result, not a consolation score; a farther 7/7 cannot displace it.
    for (const finalist of this.m_visibilityFinalists) {
      if (finalist.slotIndex < 0) break;
      const score = this.visibilityScoreForBase(finalist.basePosition, null);
      if (score < VISIBILITY_ACCEPTABLE_SAMPLE_COUNT) continue;
      this.m_visibilitySearchFoundImprovement = true;
      return finalist.slotIndex;
    }
    return this.m_visibilitySlotIndex;
  }

  private insertVisibilityFinalist(
    slotIndex: number,
    cost: number,
    basePosition: Vector3,
  ): void {
    let insertAt = -1;
    for (let index = 0; index < this.m_visibilityFinalists.length; index += 1) {
      if (cost < this.m_visibilityFinalists[index].cost) {
        insertAt = index;
        break;
      }
    }
    if (insertAt < 0) return;

    for (
      let index = this.m_visibilityFinalists.length - 1;
      index > insertAt;
      index -= 1
    ) {
      const source = this.m_visibilityFinalists[index - 1];
      const destination = this.m_visibilityFinalists[index];
      destination.slotIndex = source.slotIndex;
      destination.cost = source.cost;
      destination.basePosition.copy(source.basePosition);
    }
    const destination = this.m_visibilityFinalists[insertAt];
    destination.slotIndex = slotIndex;
    destination.cost = cost;
    destination.basePosition.copy(basePosition);
  }

  private visibilityCandidateCost(
    slot: SparkVisibilitySlot,
    basePosition: Vector3,
    preferCurrentPose: boolean,
  ): number {
    // Occlusion recovery prioritizes physical travel so a nearby upward step
    // beats walking around an object. A deliberate viewpoint replan does the
    // opposite: it returns to the new social home instead of making a token
    // one-ring adjustment and then treating that as the new long-term plan.
    if (preferCurrentPose) {
      // Keep recovery genuinely nearest. The tiny home term settles
      // numerically-equal choices but cannot turn a meaningfully farther slot
      // into the winner.
      return basePosition.distanceTo(this.m_visibilityCurrentBase) +
        basePosition.distanceTo(this.m_visibilityHomeBase) * 0.0001;
    }

    let cost = basePosition.distanceTo(this.m_visibilityHomeBase);
    cost += basePosition.distanceTo(this.m_visibilityCurrentBase) * 0.18;

    this.m_visibilityDisplacement
      .copy(basePosition)
      .sub(this.m_visibilityHomeBase);
    const candidateSide = Math.sign(
      this.m_visibilityDisplacement.dot(this.m_visibilityCameraRight),
    );
    this.m_visibilityDisplacement
      .copy(this.m_visibilityCurrentBase)
      .sub(this.m_visibilityHomeBase);
    const currentSide = Math.sign(
      this.m_visibilityDisplacement.dot(this.m_visibilityCameraRight),
    );
    if (candidateSide !== 0 && currentSide !== 0 && candidateSide !== currentSide) {
      cost += 0.18;
    }
    if (slot.forwardDepthM < SPARK_DISTANCE_FROM_USER) {
      cost += 0.12;
    }
    if (visibilitySlotsEqual(slot, this.m_visibilityActiveSlot)) cost -= 0.15;
    return cost;
  }

  /**
   * Add concentric screen-space candidates around the currently rendered head
   * before the coarse social fan. When a motorcycle seat hides the lower face,
   * this gives the solver a nearby upward answer instead of only far left/right
   * answers. Exact panel-edge candidates are added from live compositor boxes.
   */
  private buildVisibilitySearchSlots(allowLocalRecovery: boolean): void {
    for (let index = 0; index < SPARK_VISIBILITY_SLOTS.length; index += 1) {
      copyVisibilitySlot(
        this.m_visibilitySearchSlots[index],
        SPARK_VISIBILITY_SLOTS[index],
      );
    }
    this.m_visibilitySearchSlotCount = SPARK_VISIBILITY_SLOTS.length;

    this.setVisibilityHeadCenterFromBase(
      this.m_visibilityCurrentBase,
      this.m_visibilityHeadCenter,
    );
    this.measureVisibilityHeadEnvelope(this.m_visibilityHeadCenter, true);
    this.m_visibilityProjection.copy(this.m_visibilityHeadCenter).project(this.m_camera);
    if (
      !Number.isFinite(this.m_visibilityProjection.x) ||
      !Number.isFinite(this.m_visibilityProjection.y)
    ) {
      return;
    }
    const referenceNdcX = this.m_visibilityProjection.x;
    const referenceNdcY = this.m_visibilityProjection.y;
    this.m_visibilityDisplacement
      .copy(this.m_visibilityHeadCenter)
      .sub(this.m_visibilityCameraPosition);
    const referenceForwardDepth = this.m_visibilityDisplacement.dot(
      this.m_visibilityForward,
    );
    if (!(referenceForwardDepth > 0.05)) return;

    const headHalfWidthNdc = Math.max(
      referenceNdcX - this.m_visibilityHeadNdcLeft,
      this.m_visibilityHeadNdcRight - referenceNdcX,
    );
    const headTopNdc = this.m_visibilityHeadNdcTop - referenceNdcY;
    const headBottomNdc = referenceNdcY - this.m_visibilityHeadNdcBottom;

    const safeNdcLeft = -VISIBILITY_SAFE_NDC + headHalfWidthNdc;
    const safeNdcRight = VISIBILITY_SAFE_NDC - headHalfWidthNdc;
    const safeNdcBottom = -VISIBILITY_SAFE_NDC + headBottomNdc;
    const safeNdcTop = VISIBILITY_SAFE_NDC - headTopNdc;
    const localStepNdc = clamp(
      Math.max(
        headHalfWidthNdc,
        (headTopNdc + headBottomNdc) * 0.5,
      ) * 0.95,
      VISIBILITY_LOCAL_SEARCH_MIN_STEP_NDC,
      VISIBILITY_LOCAL_SEARCH_MAX_STEP_NDC,
    );
    if (allowLocalRecovery) {
      const radialStepCount =
        VISIBILITY_LOCAL_SEARCH_RING_COUNT *
        VISIBILITY_LOCAL_SEARCH_SUBDIVISIONS;
      for (let radialStep = 1; radialStep <= radialStepCount; radialStep += 1) {
        const radius = radialStep / VISIBILITY_LOCAL_SEARCH_SUBDIVISIONS;
        for (const [directionX, directionY] of VISIBILITY_LOCAL_SEARCH_DIRECTIONS) {
          this.appendVisibilitySearchNdcSlot(
            referenceNdcX + directionX * localStepNdc * radius,
            referenceNdcY + directionY * localStepNdc * radius,
            referenceForwardDepth,
            safeNdcLeft,
            safeNdcRight,
            safeNdcBottom,
            safeNdcTop,
          );
        }
      }
    }

    if (
      this.m_visibilityScreenOccluderCount === 0 ||
      this.m_visibilityCanvasWidth <= 0 ||
      this.m_visibilityCanvasHeight <= 0
    ) {
      return;
    }

    const referenceClientX = this.visibilityClientX(referenceNdcX);
    const referenceClientY = this.visibilityClientY(referenceNdcY);
    const headHalfWidthPx = headHalfWidthNdc * this.m_visibilityCanvasWidth * 0.5;
    const headTopPx = headTopNdc * this.m_visibilityCanvasHeight * 0.5;
    const headBottomPx = headBottomNdc * this.m_visibilityCanvasHeight * 0.5;
    const safeLeft = this.visibilityClientX(safeNdcLeft);
    const safeRight = this.visibilityClientX(safeNdcRight);
    const safeTop = this.visibilityClientY(safeNdcTop);
    const safeBottom = this.visibilityClientY(safeNdcBottom);
    const sideY = clamp(referenceClientY, safeTop, safeBottom);
    const verticalX = clamp(referenceClientX, safeLeft, safeRight);
    const panelClearance =
      VISIBILITY_SCREEN_GAP_PX + VISIBILITY_SCREEN_PLACEMENT_EPSILON_PX;

    for (let index = 0; index < this.m_visibilityScreenOccluderCount; index += 1) {
      const rect = this.m_visibilityScreenOccluders[index];
      this.appendVisibilitySearchSlot(
        rect.x - panelClearance - headHalfWidthPx,
        sideY,
        referenceForwardDepth,
        safeLeft,
        safeRight,
        safeTop,
        safeBottom,
      );
      this.appendVisibilitySearchSlot(
        rect.x + rect.width + panelClearance + headHalfWidthPx,
        sideY,
        referenceForwardDepth,
        safeLeft,
        safeRight,
        safeTop,
        safeBottom,
      );
      this.appendVisibilitySearchSlot(
        verticalX,
        rect.y - panelClearance - headBottomPx,
        referenceForwardDepth,
        safeLeft,
        safeRight,
        safeTop,
        safeBottom,
      );
      this.appendVisibilitySearchSlot(
        verticalX,
        rect.y + rect.height + panelClearance + headTopPx,
        referenceForwardDepth,
        safeLeft,
        safeRight,
        safeTop,
        safeBottom,
      );
    }
  }

  private appendVisibilitySearchSlot(
    clientX: number,
    clientY: number,
    forwardDepthM: number,
    safeLeft: number,
    safeRight: number,
    safeTop: number,
    safeBottom: number,
  ): void {
    if (
      this.m_visibilitySearchSlotCount >= this.m_visibilitySearchSlots.length ||
      clientX < safeLeft ||
      clientX > safeRight ||
      clientY < safeTop ||
      clientY > safeBottom
    ) {
      return;
    }
    const ndcX = ((clientX - this.m_visibilityCanvasLeft) /
      this.m_visibilityCanvasWidth) * 2 - 1;
    const ndcY = 1 - ((clientY - this.m_visibilityCanvasTop) /
      this.m_visibilityCanvasHeight) * 2;

    this.appendVisibilitySearchNdcSlot(
      ndcX,
      ndcY,
      forwardDepthM,
      -Infinity,
      Infinity,
      -Infinity,
      Infinity,
    );
  }

  private appendVisibilitySearchNdcSlot(
    ndcX: number,
    ndcY: number,
    forwardDepthM: number,
    safeLeft: number,
    safeRight: number,
    safeBottom: number,
    safeTop: number,
  ): void {
    if (
      this.m_visibilitySearchSlotCount >= this.m_visibilitySearchSlots.length ||
      ndcX < safeLeft ||
      ndcX > safeRight ||
      ndcY < safeBottom ||
      ndcY > safeTop
    ) {
      return;
    }

    // Nested chat/play boxes often propose the same edge. One candidate is
    // enough; de-duplicating here keeps the rare full search bounded.
    for (
      let index = SPARK_VISIBILITY_SLOTS.length;
      index < this.m_visibilitySearchSlotCount;
      index += 1
    ) {
      const existing = this.m_visibilitySearchSlots[index];
      if (
        existing.ndcX !== null &&
        existing.ndcY !== null &&
        Math.abs(existing.ndcX - ndcX) < VISIBILITY_SEARCH_SLOT_DEDUP_NDC &&
        Math.abs(existing.ndcY - ndcY) < VISIBILITY_SEARCH_SLOT_DEDUP_NDC
      ) {
        return;
      }
    }

    const destination = this.m_visibilitySearchSlots[this.m_visibilitySearchSlotCount];
    destination.ndcX = ndcX;
    destination.ndcY = ndcY;
    destination.forwardDepthM = forwardDepthM;
    this.m_visibilitySearchSlotCount += 1;
  }

  private visibilityClientX(ndcX: number): number {
    return this.m_visibilityCanvasLeft + (ndcX + 1) * 0.5 * this.m_visibilityCanvasWidth;
  }

  private visibilityClientY(ndcY: number): number {
    return this.m_visibilityCanvasTop + (1 - ndcY) * 0.5 * this.m_visibilityCanvasHeight;
  }

  /** Snapshot moving DOM boxes once so every candidate sees one geometry. */
  private refreshVisibilityScreenOccluders(): void {
    this.m_visibilityScreenOccluderCount = 0;
    const read = this.m_options.screenOccluders;
    if (!read) return;

    const canvasRect = this.m_engine.canvas.getBoundingClientRect();
    this.m_visibilityCanvasLeft = canvasRect.left;
    this.m_visibilityCanvasTop = canvasRect.top;
    this.m_visibilityCanvasWidth = canvasRect.width;
    this.m_visibilityCanvasHeight = canvasRect.height;
    if (!(canvasRect.width > 0) || !(canvasRect.height > 0)) return;

    const source = read();
    for (const rect of source) {
      if (this.m_visibilityScreenOccluderCount >= VISIBILITY_MAX_SCREEN_OCCLUDERS) break;
      if (
        !Number.isFinite(rect.x) ||
        !Number.isFinite(rect.y) ||
        !Number.isFinite(rect.width) ||
        !Number.isFinite(rect.height) ||
        rect.width <= 0 ||
        rect.height <= 0
      ) {
        continue;
      }
      const left = Math.max(canvasRect.left, rect.x);
      const top = Math.max(canvasRect.top, rect.y);
      const right = Math.min(canvasRect.right, rect.x + rect.width);
      const bottom = Math.min(canvasRect.bottom, rect.y + rect.height);
      if (right <= left || bottom <= top) continue;
      const destination =
        this.m_visibilityScreenOccluders[this.m_visibilityScreenOccluderCount];
      destination.x = left;
      destination.y = top;
      destination.width = right - left;
      destination.height = bottom - top;
      this.m_visibilityScreenOccluderCount += 1;
    }
  }

  /** Track motion separately from distance: one long orbit produces one plan. */
  private updateStandingCameraMotionState(): boolean {
    if (!this.m_hasStandingCameraMotionSample) {
      this.m_standingLastCameraPosition.copy(this.m_visibilityCameraPosition);
      this.m_standingLastCameraForward.copy(this.m_visibilityForward);
      this.copyStandingCameraProjection(this.m_standingLastCameraProjection);
      this.m_standingCameraLastMotionS = this.m_timeS;
      this.m_hasStandingCameraMotionSample = true;
      return false;
    }
    const translated =
      this.m_standingLastCameraPosition.distanceTo(this.m_visibilityCameraPosition) >=
      STANDING_CAMERA_MOTION_TRANSLATION_M;
    const turned =
      this.m_standingLastCameraForward.dot(this.m_visibilityForward) <
      STANDING_CAMERA_MOTION_FORWARD_DOT;
    const projectionChanged = this.hasStandingCameraProjectionChanged(
      this.m_standingLastCameraProjection,
      STANDING_CAMERA_MOTION_PROJECTION_EPSILON,
    );
    if (translated || turned || projectionChanged) {
      this.m_standingCameraLastMotionS = this.m_timeS;
    }
    this.m_standingLastCameraPosition.copy(this.m_visibilityCameraPosition);
    this.m_standingLastCameraForward.copy(this.m_visibilityForward);
    this.copyStandingCameraProjection(this.m_standingLastCameraProjection);
    return this.m_timeS - this.m_standingCameraLastMotionS >= STANDING_CAMERA_SETTLE_S;
  }

  private shouldReplanStandingForViewpoint(forceReplan = false): boolean {
    if (this.m_hasFailedStandingViewpoint) {
      const viewpointChanged =
        this.m_failedStandingViewpointCameraPosition.distanceTo(
          this.m_visibilityCameraPosition,
        ) >= STANDING_FAILED_VIEWPOINT_TRANSLATION_M ||
        this.m_failedStandingViewpointCameraForward.dot(
          this.m_visibilityForward,
        ) < STANDING_FAILED_VIEWPOINT_FORWARD_DOT;
      const projectionChanged = this.hasFailedStandingProjectionChanged();
      const hardInvalidationAtNewViewpoint =
        forceReplan &&
        (this.m_failedStandingViewpointCameraPosition.distanceTo(
          this.m_visibilityCameraPosition,
        ) >= STANDING_CAMERA_MOTION_TRANSLATION_M ||
          this.m_failedStandingViewpointCameraForward.dot(
            this.m_visibilityForward,
          ) < STANDING_CAMERA_MOTION_FORWARD_DOT ||
          projectionChanged);
      if (!viewpointChanged && !hardInvalidationAtNewViewpoint) return false;
      this.m_hasFailedStandingViewpoint = false;
    }
    if (forceReplan) return true;
    if (!this.m_hasStandingPlanReference) return true;
    const cameraTranslation =
      this.m_standingPlanCameraPosition.distanceTo(
        this.m_visibilityCameraPosition,
      );
    if (
      cameraTranslation >= STANDING_REPLAN_TRANSLATION_M ||
      this.m_standingPlanCameraForward.dot(this.m_visibilityForward) <
        STANDING_REPLAN_FORWARD_DOT
    ) {
      return true;
    }

    // Near/far describes a CHANGE in the user's relationship to the held
    // anchor, not a requirement that every fallback itself sit inside that
    // band. A clear one-metre panel-edge fit must not replan every time the
    // relocation lock expires while the camera remains stationary.
    if (cameraTranslation < STANDING_REPLAN_PROXIMITY_CAMERA_TRANSLATION_M) {
      return false;
    }

    this.m_visibilityCurrentBase.copy(this.m_cachedBaseStandingPosition);
    this.m_visibilityCurrentBase.y += this.m_visibilityVerticalOffsetTarget.y;
    this.m_visibilityDisplacement
      .copy(this.m_visibilityCameraPosition)
      .sub(this.m_visibilityCurrentBase);
    const distance = this.m_visibilityDisplacement.length();
    if (
      distance < STANDING_REPLAN_NEAR_DISTANCE_M ||
      distance > STANDING_REPLAN_FAR_DISTANCE_M
    ) {
      return true;
    }
    this.m_visibilityDisplacement.setY(0);
    const horizontalDistance = this.m_visibilityDisplacement.length();
    if (
      horizontalDistance <= 0.001 ||
      this.m_standingPlanCameraFromAnchor.lengthSq() <= 0.001
    ) {
      return false;
    }
    this.m_visibilityDisplacement.multiplyScalar(1 / horizontalDistance);
    return (
      this.m_standingPlanCameraFromAnchor.dot(this.m_visibilityDisplacement) <
      STANDING_REPLAN_AROUND_ANCHOR_DOT
    );
  }

  private captureStandingPlanReference(): void {
    this.m_camera.getWorldPosition(this.m_standingPlanCameraPosition);
    this.m_camera.getWorldDirection(this.m_standingPlanCameraForward).normalize();
    this.m_standingPlanCameraFromAnchor
      .copy(this.m_standingPlanCameraPosition)
      .sub(this.m_cachedBaseStandingPosition)
      .setY(0);
    if (this.m_standingPlanCameraFromAnchor.lengthSq() > 0.001) {
      this.m_standingPlanCameraFromAnchor.normalize();
    }
    this.m_standingLastCameraPosition.copy(this.m_standingPlanCameraPosition);
    this.m_standingLastCameraForward.copy(this.m_standingPlanCameraForward);
    this.copyStandingCameraProjection(this.m_standingLastCameraProjection);
    this.m_standingCameraLastMotionS = this.m_timeS;
    this.m_hasStandingCameraMotionSample = true;
    this.m_hasStandingPlanReference = true;
  }

  private markFailedStandingViewpoint(): void {
    this.m_failedStandingViewpointCameraPosition.copy(
      this.m_visibilityCameraPosition,
    );
    this.m_failedStandingViewpointCameraForward.copy(this.m_visibilityForward);
    const projection = this.m_camera.projectionMatrix.elements;
    for (let index = 0; index < projection.length; index += 1) {
      this.m_failedStandingViewpointProjection[index] = projection[index];
    }
    this.m_hasFailedStandingViewpoint = true;
  }

  private hasFailedStandingProjectionChanged(): boolean {
    return this.hasStandingCameraProjectionChanged(
      this.m_failedStandingViewpointProjection,
      1e-6,
    );
  }

  private hasStandingCameraProjectionChanged(
    reference: Float32Array,
    epsilon: number,
  ): boolean {
    const projection = this.m_camera.projectionMatrix.elements;
    for (let index = 0; index < projection.length; index += 1) {
      if (Math.abs(projection[index] - reference[index]) > epsilon) return true;
    }
    return false;
  }

  private copyStandingCameraProjection(destination: Float32Array): void {
    const projection = this.m_camera.projectionMatrix.elements;
    for (let index = 0; index < projection.length; index += 1) {
      destination[index] = projection[index];
    }
  }

  private syncRenderVisibility(): void {
    const visible = this.m_isVisible && !this.m_isScreenOcclusionSuppressed;
    if (this.m_root.visible === visible) return;
    this.m_root.visible = visible;
    this.m_engine.requestRender();
  }

  /**
   * Whether a frame drawn now would show any of Spark — what gates its render
   * on demand sources. False when hidden, when an opaque surface covers the
   * viewport (`ThreeEngine.isViewportCovered` — a guided run's media-only
   * step), or when nothing of it is inside the view frustum.
   *
   * Every way back to "seen" draws on its own: a camera move asks for a
   * frame, uncovering is a step change that builds or shows a scene, and the
   * next frame Spark is in view its idle source resumes.
   */
  private isSeenOnScreen(): boolean {
    if (!this.m_root.visible) return false;
    if (this.m_engine.isViewportCovered()) return false;
    const camera = this.m_camera;
    this.m_renderViewProjection.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse,
    );
    this.m_renderFrustum.setFromProjectionMatrix(this.m_renderViewProjection);
    // Eye lasers live on the scene, not under the root — a beam crossing the
    // view is Spark on screen even with its head out of shot.
    for (const laser of this.m_eyeLasers) {
      if (laser.visible) return true;
    }
    this.m_renderBounds.setFromObject(this.m_root);
    // Nothing loaded yet: nothing to draw. Each asset asks for a frame as it lands.
    if (this.m_renderBounds.isEmpty()) return false;
    this.m_renderBounds.expandByScalar(K_RENDER_BOUNDS_MARGIN_M);
    return this.m_renderFrustum.intersectsBox(this.m_renderBounds);
  }

  private readVisibilityCameraBasis(): void {
    this.m_camera.getWorldPosition(this.m_visibilityCameraPosition);
    this.m_camera.getWorldDirection(this.m_visibilityForward).normalize();
    this.m_visibilitySocialRight.crossVectors(this.m_visibilityForward, UP);
    if (this.m_visibilitySocialRight.lengthSq() < DIRECTION_EPSILON_SQ) {
      this.m_visibilitySocialRight.setFromMatrixColumn(this.m_camera.matrixWorld, 0);
    }
    this.m_visibilitySocialRight.normalize();
    this.m_visibilityCameraRight
      .setFromMatrixColumn(this.m_camera.matrixWorld, 0)
      .normalize();
    this.m_visibilityCameraUp
      .setFromMatrixColumn(this.m_camera.matrixWorld, 1)
      .normalize();
  }

  private calculateVisibilitySlotBaseFromCurrentBasis(
    slot: SparkVisibilitySlot,
    targetHeight: number,
    out: Vector3,
  ): void {
    out
      .copy(this.m_visibilityCameraPosition)
      .addScaledVector(this.m_visibilityForward, SPARK_DISTANCE_FROM_USER)
      .addScaledVector(this.m_visibilitySocialRight, SPARK_LATERAL_OFFSET_FROM_USER);
    out.y = targetHeight;

    if (slot.ndcX === null && slot.ndcY === null) return;

    // A null axis preserves the exact social home's projection on that axis.
    // Unprojection (rather than analytic FOV math) honors camera view offsets.
    this.setVisibilityHeadCenterFromBase(out, this.m_visibilityProjection);
    this.m_visibilityProjection.project(this.m_camera);
    const ndcX = slot.ndcX ?? this.m_visibilityProjection.x;
    const ndcY = slot.ndcY ?? this.m_visibilityProjection.y;
    this.m_visibilityUnprojectedPoint
      .set(ndcX, ndcY, 0.5)
      .unproject(this.m_camera);
    this.m_visibilityRayDirection
      .copy(this.m_visibilityUnprojectedPoint)
      .sub(this.m_visibilityCameraPosition)
      .normalize();
    const forwardAmount = this.m_visibilityRayDirection.dot(
      this.m_visibilityForward,
    );
    if (forwardAmount <= 0.05) return;

    this.m_visibilityHeadCenter
      .copy(this.m_visibilityCameraPosition)
      .addScaledVector(
        this.m_visibilityRayDirection,
        slot.forwardDepthM / forwardAmount,
      );
    out
      .copy(this.m_visibilityHeadCenter);
    this.subtractCurrentBodyOffsets(out);
    out.addScaledVector(UP, -HEAD_BASE_PIVOT_HEIGHT);
    // Horizontal slots stay under the existing effective-height path. A panel
    // edge slot may deliberately lift/lower the whole standing anchor; that
    // vertical delta is smoothed as a separate visibility-placement layer.
    if (slot.ndcY === null) out.y = targetHeight;
  }

  private setVisibilityHeadCenterFromBase(base: Vector3, out: Vector3): void {
    out.copy(base);
    this.addCurrentBodyOffsets(out);
    out.y = base.y + HEAD_BASE_PIVOT_HEIGHT;
  }

  private visibilityScoreForBase(
    base: Vector3,
    centerIsKnownClear: boolean | null,
  ): number {
    if (base.y < SPARK_MIN_STANDING_Y) return 0;
    this.setVisibilityHeadCenterFromBase(base, this.m_visibilityHeadCenter);
    return this.visibilityScoreAtHeadCenterForDestination(
      this.m_visibilityHeadCenter,
      centerIsKnownClear,
    );
  }

  /** A destination must clear both today's pose and the gaze-safe reserve. */
  private visibilityScoreAtHeadCenterForDestination(
    headCenter: Vector3,
    liveCenterIsKnownClear: boolean | null = null,
  ): number {
    const liveScore = this.visibilityScoreAtHeadCenter(
      headCenter,
      liveCenterIsKnownClear,
    );
    if (liveScore < VISIBILITY_ACCEPTABLE_SAMPLE_COUNT) return liveScore;
    return Math.min(
      liveScore,
      this.visibilityScoreAtHeadCenter(headCenter, null, true),
    );
  }

  /** Rotate one cheap edge sample through the visible skull between searches. */
  private isNextVisibilityEnvelopeSampleOccluded(headCenter: Vector3): boolean {
    const sampleIndex = this.m_visibilityEdgeProbeIndex;
    this.m_visibilityEdgeProbeIndex =
      (sampleIndex + 1) % (VISIBILITY_SAMPLE_COUNT - 1);
    return this.isVisibilitySkullSampleOccluded(headCenter, sampleIndex + 1);
  }

  private visibilityScoreAtHeadCenter(
    headCenter: Vector3,
    centerIsKnownClear: boolean | null,
    reserveAllHeadPoses = false,
  ): number {
    if (
      !this.measureVisibilityHeadEnvelope(headCenter, reserveAllHeadPoses) ||
      this.visibilityHeadEnvelopeOverlapsScreenOccluder()
    ) {
      return 0;
    }

    if (centerIsKnownClear === false) return 0;
    if (
      centerIsKnownClear === null &&
      this.isVisibilitySkullSampleOccluded(
        headCenter,
        0,
        reserveAllHeadPoses,
      )
    ) {
      return 0;
    }
    let score = 1;
    let remainingSamples = VISIBILITY_SAMPLE_COUNT - 1;
    for (let sampleIndex = 1; sampleIndex < VISIBILITY_SAMPLE_COUNT; sampleIndex += 1) {
      if (
        !this.isVisibilitySkullSampleOccluded(
          headCenter,
          sampleIndex,
          reserveAllHeadPoses,
        )
      ) {
        score += 1;
      }
      remainingSamples -= 1;
      if (score + remainingSamples < VISIBILITY_ACCEPTABLE_SAMPLE_COUNT) return score;
    }
    return score;
  }

  private isVisibilitySkullSampleOccluded(
    headCenter: Vector3,
    sampleIndex: number,
    reserveAllHeadPoses = false,
  ): boolean {
    this.setVisibilitySkullSamplePoint(
      headCenter,
      sampleIndex,
      this.m_visibilitySamplePoint,
      reserveAllHeadPoses,
    );
    return this.isVisibilitySampleOccluded(this.m_visibilitySamplePoint);
  }

  /** Transform an interior neutral-skull landmark through the live head pose. */
  private setVisibilitySkullSamplePoint(
    headCenter: Vector3,
    sampleIndex: number,
    out: Vector3,
    reserveAllHeadPoses = false,
  ): void {
    if (reserveAllHeadPoses) {
      const reserveOffset =
        VISIBILITY_SKULL_RESERVED_SAMPLE_OFFSETS[sampleIndex] ??
        VISIBILITY_SKULL_RESERVED_SAMPLE_OFFSETS[0];
      out
        .copy(headCenter)
        .addScaledVector(this.m_visibilityCameraRight, reserveOffset[0])
        .addScaledVector(this.m_visibilityCameraUp, reserveOffset[1]);
      return;
    }
    const offset = VISIBILITY_SKULL_SAMPLE_OFFSETS[sampleIndex] ??
      VISIBILITY_SKULL_SAMPLE_OFFSETS[0];
    this.m_visibilityEnvelopeRoot
      .copy(headCenter)
      .addScaledVector(UP, -HEAD_BASE_PIVOT_HEIGHT);
    out
      .set(
        VISIBILITY_SKULL_SAMPLE_CENTER_LOCAL[0] + offset[0],
        VISIBILITY_SKULL_SAMPLE_CENTER_LOCAL[1] + offset[1],
        VISIBILITY_SKULL_SAMPLE_CENTER_LOCAL[2],
      )
      .applyEuler(this.m_headPivot.rotation)
      .add(this.m_headPivot.position)
      .applyEuler(this.m_neck.rotation)
      .add(this.m_visibilityEnvelopeRoot);
  }

  /**
   * Project the live-pose, opaque-skull oriented box. The source box was
   * clipped at the shader's jaw cutoff, so pitch/yaw can move visible face
   * geometry without ever bringing the intentionally faded bust back in.
   */
  private measureVisibilityHeadEnvelope(
    headCenter: Vector3,
    reserveAllHeadPoses = false,
  ): boolean {
    this.m_visibilityHeadScreenLeft = Infinity;
    this.m_visibilityHeadScreenTop = Infinity;
    this.m_visibilityHeadScreenRight = -Infinity;
    this.m_visibilityHeadScreenBottom = -Infinity;
    this.m_visibilityHeadNdcLeft = Infinity;
    this.m_visibilityHeadNdcTop = -Infinity;
    this.m_visibilityHeadNdcRight = -Infinity;
    this.m_visibilityHeadNdcBottom = Infinity;
    this.m_visibilityHeadEnvelopeProjectable = true;
    this.m_visibilityEnvelopeRoot
      .copy(headCenter)
      .addScaledVector(UP, -HEAD_BASE_PIVOT_HEIGHT);

    let inFrame = true;
    if (reserveAllHeadPoses) {
      for (const side of [-1, 1]) {
        for (const height of [
          -VISIBILITY_SKULL_POSE_RESERVE_BOTTOM_M,
          VISIBILITY_SKULL_POSE_RESERVE_TOP_M,
        ]) {
          this.m_visibilitySamplePoint
            .copy(headCenter)
            .addScaledVector(
              this.m_visibilityCameraRight,
              side * VISIBILITY_SKULL_POSE_RESERVE_HALF_WIDTH_M,
            )
            .addScaledVector(this.m_visibilityCameraUp, height);
          if (!this.includeVisibilityEnvelopePoint(this.m_visibilitySamplePoint)) {
            inFrame = false;
          }
        }
      }
      return inFrame;
    }

    for (let xIndex = 0; xIndex < 2; xIndex += 1) {
      const nativeX = xIndex === 0
        ? VISIBILITY_SKULL_NATIVE_MIN[0]
        : VISIBILITY_SKULL_NATIVE_MAX[0];
      for (let yIndex = 0; yIndex < 2; yIndex += 1) {
        const nativeY = yIndex === 0
          ? VISIBILITY_SKULL_NATIVE_MIN[1]
          : VISIBILITY_SKULL_NATIVE_MAX[1];
        for (let zIndex = 0; zIndex < 2; zIndex += 1) {
          const nativeZ = zIndex === 0
            ? VISIBILITY_SKULL_NATIVE_MIN[2]
            : VISIBILITY_SKULL_NATIVE_MAX[2];
          // HEAD_LOCAL_YAW_RAD is +90°: native +X (face) maps to local -Z,
          // while native +Z (skull width) maps to local +X.
          this.m_visibilitySamplePoint
            .set(
              HEAD_LOCAL_POSITION[0] + nativeZ * HEAD_LOCAL_SCALE,
              HEAD_LOCAL_POSITION[1] + nativeY * HEAD_LOCAL_SCALE,
              HEAD_LOCAL_POSITION[2] - nativeX * HEAD_LOCAL_SCALE,
            )
            .applyEuler(this.m_headPivot.rotation)
            .add(this.m_headPivot.position)
            .applyEuler(this.m_neck.rotation)
            .add(this.m_visibilityEnvelopeRoot);
          if (!this.includeVisibilityEnvelopePoint(this.m_visibilitySamplePoint)) {
            inFrame = false;
          }
        }
      }
    }
    return inFrame;
  }

  private includeVisibilityEnvelopePoint(point: Vector3): boolean {
    this.m_visibilityProjection.copy(point).project(this.m_camera);
    const finite =
      Number.isFinite(this.m_visibilityProjection.x) &&
      Number.isFinite(this.m_visibilityProjection.y) &&
      Number.isFinite(this.m_visibilityProjection.z);
    if (!finite) {
      this.m_visibilityHeadEnvelopeProjectable = false;
      return false;
    }

    const inDepthRange =
      this.m_visibilityProjection.z >= -1 &&
      this.m_visibilityProjection.z <= 1;
    if (!inDepthRange) this.m_visibilityHeadEnvelopeProjectable = false;

    this.m_visibilityHeadNdcLeft = Math.min(
      this.m_visibilityHeadNdcLeft,
      this.m_visibilityProjection.x,
    );
    this.m_visibilityHeadNdcTop = Math.max(
      this.m_visibilityHeadNdcTop,
      this.m_visibilityProjection.y,
    );
    this.m_visibilityHeadNdcRight = Math.max(
      this.m_visibilityHeadNdcRight,
      this.m_visibilityProjection.x,
    );
    this.m_visibilityHeadNdcBottom = Math.min(
      this.m_visibilityHeadNdcBottom,
      this.m_visibilityProjection.y,
    );

    const inFrame =
      inDepthRange &&
      Math.abs(this.m_visibilityProjection.x) <= VISIBILITY_SAFE_NDC &&
      Math.abs(this.m_visibilityProjection.y) <= VISIBILITY_SAFE_NDC;

    const clientX = this.visibilityClientX(this.m_visibilityProjection.x);
    const clientY = this.visibilityClientY(this.m_visibilityProjection.y);
    this.m_visibilityHeadScreenLeft = Math.min(
      this.m_visibilityHeadScreenLeft,
      clientX,
    );
    this.m_visibilityHeadScreenTop = Math.min(
      this.m_visibilityHeadScreenTop,
      clientY,
    );
    this.m_visibilityHeadScreenRight = Math.max(
      this.m_visibilityHeadScreenRight,
      clientX,
    );
    this.m_visibilityHeadScreenBottom = Math.max(
      this.m_visibilityHeadScreenBottom,
      clientY,
    );
    return inFrame;
  }

  private visibilityHeadEnvelopeOverlapsScreenOccluder(): boolean {
    for (let index = 0; index < this.m_visibilityScreenOccluderCount; index += 1) {
      const rect = this.m_visibilityScreenOccluders[index];
      const gap = VISIBILITY_SCREEN_GAP_PX;
      if (
        this.m_visibilityHeadScreenRight > rect.x - gap &&
        this.m_visibilityHeadScreenLeft < rect.x + rect.width + gap &&
        this.m_visibilityHeadScreenBottom > rect.y - gap &&
        this.m_visibilityHeadScreenTop < rect.y + rect.height + gap
      ) {
        return true;
      }
    }
    return false;
  }

  private isVisibilitySampleOccluded(point: Vector3): boolean {
    const picker = this.m_pickingService;
    if (!picker) return false;
    this.m_visibilityRayDirection
      .copy(point)
      .sub(this.m_visibilityCameraPosition);
    const sampleDistance = this.m_visibilityRayDirection.length();
    if (sampleDistance <= VISIBILITY_OCCLUSION_EPSILON_M) return false;
    this.m_visibilityRayDirection.multiplyScalar(1 / sampleDistance);
    this.m_visibilityRay.set(
      this.m_visibilityCameraPosition,
      this.m_visibilityRayDirection,
    );
    const hit = picker.pickFromRay(
      this.m_visibilityRay,
      this.m_isDepthOccludingSceneNode,
    );
    return (
      hit !== null &&
      hit.distance < sampleDistance - VISIBILITY_OCCLUSION_EPSILON_M
    );
  }

  /** Head aligned with the camera height (`ComputeDynamicBaseHeight`). */
  private computeDynamicBaseHeight(): number {
    const cameraY = this.m_camera.getWorldPosition(new Vector3()).y;
    return Math.max(
      SPARK_MIN_STANDING_Y,
      cameraY - HEAD_BASE_PIVOT_HEIGHT + SPARK_HEIGHT_OFFSET,
    );
  }

  private calculateEffectiveHeight(dt: number): number {
    const dynamicBaseHeight = this.m_cachedDynamicBaseHeight;

    if (!this.m_currentObjectOfInterest) {
      this.m_currentEffectiveHeight = lerp(
        this.m_currentEffectiveHeight,
        dynamicBaseHeight,
        clamp01(dt * SECONDARY_BODY_ADJUSTMENT_SPEED),
      );
      return this.m_currentEffectiveHeight;
    }

    const objectPos = worldPositionOf(this.m_currentObjectOfInterest);
    const objectSize = this.cachedCombinedMeshSize(this.m_currentObjectOfInterest);
    const distanceToObject = this.m_root.position.distanceTo(objectPos);
    const objectHeight = objectPos.y;

    const shouldKneel =
      ENABLE_KNEELING &&
      objectSize > 0 &&
      objectSize < SMALL_OBJECT_THRESHOLD_M &&
      distanceToObject < KNEELING_DISTANCE_THRESHOLD_M &&
      objectHeight < KNEELING_MAX_HEIGHT_M;

    if (shouldKneel && !this.m_wasKneelingConditionMet) {
      this.m_lastKneelingTriggerTimeS = this.m_timeS;
      this.m_shouldDelayKneeling = true;
    } else if (!shouldKneel) {
      this.m_shouldDelayKneeling = false;
    }
    this.m_wasKneelingConditionMet = shouldKneel;

    let targetHeight = dynamicBaseHeight;
    if (shouldKneel) {
      if (this.m_shouldDelayKneeling) {
        if (this.m_timeS - this.m_lastKneelingTriggerTimeS >= KNEELING_DELAY_S) {
          this.m_shouldDelayKneeling = false;
          targetHeight = dynamicBaseHeight - KNEELING_HEIGHT_REDUCTION_M;
        }
      } else {
        targetHeight = dynamicBaseHeight - KNEELING_HEIGHT_REDUCTION_M;
      }
    }

    const kneelingLerpSpeed = clamp01(dt * SECONDARY_BODY_ADJUSTMENT_SPEED);
    this.m_currentEffectiveHeight = lerp(
      this.m_currentEffectiveHeight,
      targetHeight,
      applyEasing(kneelingLerpSpeed, EASING_STRENGTH),
    );
    return this.m_currentEffectiveHeight;
  }

  private calculateSecondaryBodyAdjustment(dt: number): void {
    if (!this.m_currentObjectOfInterest) return;

    // Delay body adjustment while the head is still turning.
    if (this.m_shouldDelaySecondaryBodyAdjustment) {
      if (this.m_timeS - this.m_lastTransitionStartTimeS < SECONDARY_BODY_ADJUSTMENT_DELAY_S) {
        return;
      }
      this.m_shouldDelaySecondaryBodyAdjustment = false;
    }

    const objectPos = worldPositionOf(this.m_currentObjectOfInterest);
    const sparkPos = this.m_root.position;
    const distanceToObject = sparkPos.distanceTo(objectPos);
    const headPitchDeg = this.calculateHeadPitchToObject(objectPos);

    const targetOffset = new Vector3();
    if (headPitchDeg > MAX_COMFORTABLE_PITCH_DOWN_DEG + PITCH_HYSTERESIS_DEG) {
      // Looking too steeply down — step back to relieve the neck.
      const excessivePitch = headPitchDeg - MAX_COMFORTABLE_PITCH_DOWN_DEG;
      const stepBack = (excessivePitch / 45) * PITCH_BACK_STEP_MULTIPLIER;
      targetOffset
        .copy(sparkPos)
        .sub(objectPos)
        .setY(0)
        .normalize()
        .multiplyScalar(stepBack);
    } else if (distanceToObject < PROXIMITY_THRESHOLD_M - PROXIMITY_DEADZONE_M) {
      const excess = PROXIMITY_THRESHOLD_M - distanceToObject;
      const scaled = clamp01(excess / PROXIMITY_THRESHOLD_M) * PROXIMITY_RESPONSE_SCALE;
      targetOffset
        .copy(sparkPos)
        .sub(objectPos)
        .setY(0)
        .normalize()
        .multiplyScalar(Math.max(0, BACKWARD_MOVEMENT_AMOUNT_M * scaled));
    } else if (distanceToObject > PROXIMITY_THRESHOLD_M + PROXIMITY_DEADZONE_M) {
      const deficit = distanceToObject - (PROXIMITY_THRESHOLD_M + PROXIMITY_DEADZONE_M);
      const scaled = clamp01(deficit / PROXIMITY_THRESHOLD_M) * PROXIMITY_RESPONSE_SCALE;
      targetOffset
        .copy(objectPos)
        .sub(sparkPos)
        .setY(0)
        .normalize()
        .multiplyScalar(Math.max(0, FORWARD_MOVEMENT_AMOUNT_M * scaled));
    } else if (HOLD_OFFSET_INSIDE_COMFORT_ZONE) {
      targetOffset.copy(this.m_currentSecondaryBodyOffset);
    }

    smoothDampVec3(
      this.m_currentSecondaryBodyOffset,
      targetOffset,
      this.m_secondaryOffsetVelocity,
      SECONDARY_OFFSET_SMOOTHING_TIME_S,
      dt,
    );
  }

  private continuouslyUpdateSecondaryBodyAdjustments(dt: number): void {
    if (!this.m_currentObjectOfInterest) return;
    this.calculateSecondaryBodyAdjustment(dt);
  }

  private cachedCombinedMeshSize(target: Object3D): number {
    if (
      this.m_sizeCacheTarget !== target ||
      this.m_timeS - this.m_sizeCacheTimeS > 0.5
    ) {
      this.m_sizeCacheTarget = target;
      this.m_sizeCacheValue = combinedMeshSize(target);
      this.m_sizeCacheTimeS = this.m_timeS;
    }
    return this.m_sizeCacheValue;
  }

  private calculateHeadPitchToObject(targetPosition: Vector3): number {
    const neckPos = worldPositionOf(this.m_neck);
    const direction = targetPosition.clone().sub(neckPos).normalize();
    const pitchDeg = -Math.asin(clamp(direction.y, -1, 1)) / DEG2RAD;
    return Math.max(0, pitchDeg);
  }

  /** Port of `UpdateSparkPosition`. */
  private updateSparkPosition(dt: number): void {
    if (this.m_root.position.y < SPARK_MIN_STANDING_Y) {
      // Correct pre-change/HMR state before the visibility planner samples it;
      // otherwise it could preserve a forbidden pose as a clear fallback.
      this.m_root.position.y = SPARK_MIN_STANDING_Y;
      this.m_sparkPositionVelocity.y = 0;
    }
    // Advance before validation so panel/frame/depth checks see the exact
    // transient pose which this same tick will ask the root spring to render.
    this.updateHeadPointerLean(dt);
    this.updateVisibilityPlacement();

    if (!this.m_hasCachedStandingPosition) {
      this.calculateStandingPosition(TMP_VEC);
      this.m_cachedBaseStandingPosition.copy(this.m_baseSparkPosition);
      this.m_hasCachedStandingPosition = true;
      this.captureStandingPlanReference();
    }

    smoothDampVec3(
      this.m_smoothedBaseStandingPosition,
      this.m_cachedBaseStandingPosition,
      this.m_basePositionVelocity,
      BASE_POSITION_SMOOTHING_TIME_S,
      dt,
    );

    this.m_baseSparkPosition.copy(this.m_smoothedBaseStandingPosition);
    this.calculateEffectiveHeight(dt);
    this.m_standingHeightTarget.y =
      Math.max(
        SPARK_MIN_STANDING_Y,
        this.m_currentEffectiveHeight + this.m_visibilityVerticalOffsetTarget.y,
      );
    smoothDampVec3(
      this.m_smoothedStandingHeight,
      this.m_standingHeightTarget,
      this.m_standingHeightVelocity,
      BASE_POSITION_SMOOTHING_TIME_S,
      dt,
    );
    this.m_smoothedStandingHeight.y = Math.max(
      SPARK_MIN_STANDING_Y,
      this.m_smoothedStandingHeight.y,
    );

    const targetPosition = TMP_VEC.copy(this.m_smoothedBaseStandingPosition);
    this.addCurrentBodyOffsets(targetPosition);
    targetPosition.y = this.m_smoothedStandingHeight.y;

    smoothDampVec3(
      this.m_root.position,
      targetPosition,
      this.m_sparkPositionVelocity,
      POSITION_SMOOTHING_TIME_S,
      dt,
    );
    if (this.m_root.position.y < SPARK_MIN_STANDING_Y) {
      // Defensive HMR/legacy-state seam: never ease upward through forbidden
      // space if an existing session already carried a below-floor value.
      this.m_root.position.y = SPARK_MIN_STANDING_Y;
      this.m_sparkPositionVelocity.y = 0;
    }
  }
}

// -- Math helpers -------------------------------------------------------------

const UP = new Vector3(0, 1, 0);
const TMP_VEC = new Vector3();
const SCRATCH_HEAD_POINTER_POINT = new Vector3();

function copyVisibilitySlot(
  target: SparkVisibilitySlot,
  source: SparkVisibilitySlot,
): void {
  target.ndcX = source.ndcX;
  target.ndcY = source.ndcY;
  target.forwardDepthM = source.forwardDepthM;
}

function visibilitySlotsEqual(
  first: SparkVisibilitySlot,
  second: SparkVisibilitySlot,
): boolean {
  return (
    first.ndcX === second.ndcX &&
    first.ndcY === second.ndcY &&
    first.forwardDepthM === second.forwardDepthM
  );
}

function isDepthOccludingMaterial(material: Material): boolean {
  return (
    material.visible &&
    material.depthTest &&
    material.depthWrite &&
    material.opacity > 0.01
  );
}

function worldPositionOf(node: Object3D): Vector3 {
  return node.getWorldPosition(new Vector3());
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp01(t);
}

/** Shortest-path angle lerp (radians) so yaw doesn't spin the long way. */
function lerpAngle(a: number, b: number, t: number): number {
  let delta = (b - a) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return a + delta * clamp01(t);
}

/** Unity `ApplyEasing` — exponential ease-out above strength 1. */
function applyEasing(t: number, strength: number): number {
  const clamped = clamp01(t);
  if (strength <= 1) return clamped;
  return 1 - Math.pow(1 - clamped, strength);
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function bezierPoint(p0: Vector3, p1: Vector3, p2: Vector3, t: number): Vector3 {
  const oneMinusT = 1 - t;
  return new Vector3()
    .addScaledVector(p0, oneMinusT * oneMinusT)
    .addScaledVector(p1, 2 * oneMinusT * t)
    .addScaledVector(p2, t * t);
}

/** Signed shortest-path delta from one angle to another (radians). */
function shortestAngleDelta(from: number, to: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

/** Port of Unity's `Vector3.SmoothDamp` (critically damped spring). */
function smoothDampVec3(
  current: Vector3,
  target: Vector3,
  velocity: Vector3,
  smoothTime: number,
  dt: number,
): void {
  const omega = 2 / Math.max(0.0001, smoothTime);
  const x = omega * dt;
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);

  for (const axis of ["x", "y", "z"] as const) {
    const change = current[axis] - target[axis];
    const originalTo = target[axis];
    const temp = (velocity[axis] + omega * change) * dt;
    velocity[axis] = (velocity[axis] - omega * temp) * exp;
    let output = originalTo + (change + temp) * exp;
    // Prevent overshoot.
    if (originalTo - current[axis] > 0 === output > originalTo) {
      output = originalTo;
      velocity[axis] = (output - originalTo) / dt;
    }
    current[axis] = output;
  }
}

/** Port of `CalculateCombinedMeshSize` — largest bounds dimension. */
function combinedMeshSize(node: Object3D): number {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  let hasBounds = false;
  node.updateWorldMatrix(true, false);
  node.traverse((child) => {
    const mesh = child as Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    const box = mesh.geometry.boundingBox;
    if (!box || box.isEmpty()) return;
    const corners = boxCorners(box.min, box.max);
    mesh.updateWorldMatrix(true, false);
    for (const corner of corners) {
      corner.applyMatrix4(mesh.matrixWorld);
      minX = Math.min(minX, corner.x); maxX = Math.max(maxX, corner.x);
      minY = Math.min(minY, corner.y); maxY = Math.max(maxY, corner.y);
      minZ = Math.min(minZ, corner.z); maxZ = Math.max(maxZ, corner.z);
    }
    hasBounds = true;
  });
  if (!hasBounds) return 0;
  return Math.max(maxX - minX, maxY - minY, maxZ - minZ);
}

function boxCorners(min: Vector3, max: Vector3): Vector3[] {
  return [
    new Vector3(min.x, min.y, min.z),
    new Vector3(min.x, min.y, max.z),
    new Vector3(min.x, max.y, min.z),
    new Vector3(min.x, max.y, max.z),
    new Vector3(max.x, min.y, min.z),
    new Vector3(max.x, min.y, max.z),
    new Vector3(max.x, max.y, min.z),
    new Vector3(max.x, max.y, max.z),
  ];
}
